import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Square, Image as ImageIcon, Package as PackageIcon, Type as TypeIcon, HelpCircle, Palette, Camera, Upload, RefreshCw } from 'lucide-react';
import styled from 'styled-components';
import { useDesignContext } from '../../hooks/useDesignContext';
import { API_BASE_URL, buildApiUrl } from '../../config/api';
import Art from './Art';
import ArtworkMapping from './ArtworkMapping';
import { getGraniteForColor } from '../../utils/colorMappings';
import Shapes from './Shapes';
import Vases from './Vases';
import Text from './Text';
import Colors from './Colors';
import Porcelain from './Porcelain';
import { persistTextToCache } from './Text';
import EsqModals from './EsqModals';
import EsqTemplates from './EsqTemplates';
import EsqText, { EsqTextFormValues } from './EsqText';
import { adjustLastNameCasing } from '../../utils/nameCasing';

// Styled Components
const ModalBody = styled.div<{ $noPadding?: boolean }>`
  padding: ${props => props.$noPadding ? '0' : '20px'};
  overflow: auto;
  scrollbar-width: none; /* Hide scrollbars for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbars for Chrome/Safari/Opera */
  }
  flex: 1 1 auto;
  min-height: 0;

  @media (max-width: 768px) {
    padding: ${props => props.$noPadding ? '0' : '12px 10px'};
  }
`;

const DesktopColorsWrapper = styled.div`
  width: 100%;
  padding-top: 10px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  color: #fff;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ButtonBar = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: row;
    padding: 10px;
    gap: 16px;
    width: 100%;
    justify-content: flex-start;
    overflow-x: auto;
  }
`;

const IconButton = styled.button<{ disabled?: boolean }>`
  width: 70px;
  height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #bccddf;
  border: none;
  border-radius: 5px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.disabled ? '#7f8c8d' : '#000000'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.25s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};
  flex-shrink: 0;

  svg {
    width: 18px !important;
    height: 18px !important;
  }

  &:hover {
    background: ${props => props.disabled ? '#bccddf' : '#5D722C'};
    color: ${props => props.disabled ? '#7f8c8d' : '#ffffff'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? '0 4px 12px rgba(0, 0, 0, 0.05)' : '0 6px 16px rgba(93, 114, 44, 0.35)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const MobileOnlyIconButton = styled(IconButton)`
  display: none;
  @media (max-width: 768px) {
    display: flex;
  }
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  padding: 10px 0;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ModeOption = styled.button<{ $active?: boolean }>`
  border: 1px solid ${p => (p.$active ? '#5D722C' : '#e2e8f0')};
  border-radius: 10px;
  padding: 24px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${p => (p.$active ? 'rgba(93, 114, 44, 0.08)' : '#f8fafc')};
  color: #0f172a;

  &:hover {
    border-color: #5D722C;
    background: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  }
`;

const ModeTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const ModeDesc = styled.div`
  font-size: 12px;
  color: #475569;
  line-height: 1.35;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQItem = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  background: #f8fafc;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
`;

const FAQQuestion = styled.h3`
  margin: 0 0 6px;
  font-size: 15px;
  color: #1f2937;
`;

const FAQAnswer = styled.p`
  margin: 0;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.5;
`;

const SupportSection = styled.div`
  width: 100%;
  padding: 8px 6px 10px;
  border-top: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  margin-top: auto;
`;

const SupportTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
  text-align: center;
`;

const SupportButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 4px;
  background: #5D722C;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 2px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(93, 114, 44, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: #4a5b23;
    box-shadow: 0 6px 12px rgba(93, 114, 44, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: block;
  z-index: 2000;
  pointer-events: auto;
`;

const ModalCard = styled.div<{ $isArtModal?: boolean; $isArtGridActive?: boolean; $isTextModal?: boolean; $isPorcelainModal?: boolean; $isEsqModal?: boolean; $isCategoryModal?: boolean; $isShapesModal?: boolean }>`
  position: fixed;
  left: 100px;
  top: 20px;
  transform: none;
  width: fit-content;
  min-width: 600px;
  min-height: 450px;
  height: fit-content;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 140px);
  max-height: calc(100vh - 40px);
  overflow: hidden;
  pointer-events: auto;
  ${props => props.$isPorcelainModal && `
    width: 380px;
    height: auto;
    max-height: calc(100vh - 40px);
    min-width: 340px;
  `}
  ${props => props.$isCategoryModal && `
    width: 360px;
    height: auto;
    min-width: 340px;
    min-height: 350px;
  `}
  ${props => props.$isShapesModal && `
    width: 360px;
    height: auto;
    min-width: 340px;
    min-height: 350px;
  `}
  ${props => props.$isArtModal && `
    width: 380px;
    height: auto;
    min-width: 200px;
    min-height: auto;
  `}
  ${props => props.$isArtGridActive && `
    width: 380px;
    height: 620px;
    min-width: 200px;
    min-height: auto;
  `}
  ${props => props.$isTextModal && `
    width: 400px;
    height: auto;
    min-width: 340px;
    min-height: auto;
  `}
  ${props => props.$isEsqModal && `
    width: 400px;
    height: auto;
    min-width: 340px;
    min-height: auto;
  `}

  @media (max-width: 768px) {
    left: 0;
    top: auto;
    bottom: 0;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: auto;
    max-height: 85vh;
    border-radius: 16px 16px 0 0;
    /* Reset specific widths for mobile */
    ${props => props.$isArtModal && `width: 100%; height: auto; max-height: 85vh; min-width: 0; min-height: 0;`}
    ${props => props.$isArtGridActive && `width: 100%; height: auto; max-height: 85vh; min-width: 0; min-height: 0;`}
    ${props => props.$isPorcelainModal && `width: 100%; height: auto; max-height: 85vh; min-width: 0; min-height: 0;`}
    ${props => props.$isEsqModal && `width: 100%; height: auto; max-height: 85vh; min-width: 0; min-height: 0;`}
    ${props => props.$isCategoryModal && `width: 100%; height: auto; max-height: 85vh; min-width: 0; min-height: 0;`}
    ${props => props.$isShapesModal && `width: 100%; height: auto; max-height: 85vh; min-width: 0; min-height: 0;`}
  }
`;

const ModalHeader = styled.div`
  padding: 10px 20px;
  background: #666666;
  color: white;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 12px 12px 0 0;
`;

const Panel = styled.div`
  width: 100px;
  align-items: center;
  background: #ffffff;
  color: #333;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome/Safari */
  }
  border-right: 2px solid #e1e5e9;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    flex-direction: row;
    border-right: none;
    border-top: 1px solid #e1e5e9;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    justify-content: space-between;
    padding-bottom: env(safe-area-inset-bottom);
  }
`;

const PanelBody = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: none; /* Hide standard panel body on mobile, use modals instead */
  }
`;



const CompatibilityModalCard = styled(ModalCard)`
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 520px;
  padding: 0;
  min-height: auto;
  height: auto;
`;

const CompatibilityModalContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const CompatibilityIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CompatibilityText = styled.div`
  flex: 1;
`;

const CompatibilityTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  color: #0f172a;
`;

const CompatibilityDescription = styled.p`
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
  line-height: 1.4;
`;

const CompatibilityActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 14px;
  border-radius: 8px;
  border: ${props => props.$primary ? 'none' : '1px solid #b8cc8a'};
  background: ${props => props.$primary
    ? '#5D722C'
    : '#eaf4d6'};
  color: ${props => props.$primary ? '#fff' : '#2f4a1c'};
  cursor: pointer;
  box-shadow: ${props => props.$primary ? '0 8px 24px rgba(93, 114, 44, 0.12)' : 'none'};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.$primary
    ? '0 12px 28px rgba(93, 114, 44, 0.18)'
    : '0 4px 12px rgba(93, 114, 44, 0.16)'};
    ${props => !props.$primary ? 'background: #dcebbd;' : ''}
  }
`;

const FAQ_ITEMS = [
  {
    question: 'How do I start a new memorial layout?',
    answer: 'Click Shapes, pick a monument shape, and the designer will load a matching granite preview for you to customize.'
  },
  {
    question: 'Where do I add portraits or emblems?',
    answer: 'After a template is selected, use the Art button. Artwork is automatically filtered by the granite color you chose.'
  },
  {
    question: 'What is the quickest way to save progress?',
    answer: 'Use the Save button in the toolbar. You’ll be prompted to sign in so the design can be stored in My Design.'
  },
  {
    question: 'Can I share a proof with a family member?',
    answer: 'Yes. Use the Share button to download a PNG, copy a secure link, or send it through email and social platforms.'
  },
  {
    question: 'How do I get help with sizing or layout?',
    answer: 'Open the Contact form in the right panel or email support@monudesign.com for guided assistance.'
  }
] as const;

// Image loading helper - single attempt, no retries to avoid request spam
const loadImageWithRetry = async (src: string, retries = 1): Promise<HTMLImageElement> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = src; // Never add cache-busters - they cause duplicate 404s on retries
      });
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
  throw new Error('All retry attempts failed');
};

// Helper functions
const getGraniteImagePath = (colorId: number | string): string => {
  return '';
};

const getArtworkType = (element: any): string => {
  if (!element || element.type !== 'artwork') return '';

  const props = element.properties || {};
  const candidates = [
    props.type,
    props.artworkType,
    props.artwork_type,
    props.artType,
    props.artwork_type_name
  ];

  for (const candidate of candidates) {
    if (candidate != null) {
      const typeString = String(candidate).toLowerCase().trim();
      if (typeString) return typeString;
    }
  }

  return '';
};

const isVaseElement = (element: any): boolean => {
  if (!element || element.type !== 'artwork') return false;

  const role = (element.properties?.role || '').toString().toLowerCase();
  const type = (element.properties?.type || '').toString().toLowerCase();

  return role === 'vase' || type === 'vase' || role === 'vases' || type === 'vases';
};

const isTemplateElement = (element: any): boolean => {
  return element?.type === 'artwork' &&
    (element.properties?.role === 'template' || element.properties?.isTemplate);
};

const isEsqTemplateLayerElement = (element: any): boolean => {
  if (!element) return false;
  const role = String(element?.properties?.role || '').toLowerCase();
  return (
    role === 'template_design' ||
    role === 'esq_fixed_artwork'
  );
};

const isUserArtElement = (element: any): boolean => {
  if (!element || element.type !== 'artwork') return false;

  const role = (element.properties?.role || '').toString().toLowerCase();
  const isTemplateRole = role === 'template' || Boolean(element.properties?.isTemplate);

  return !isTemplateRole && role !== 'vase';
};

const isTextElement = (element: any): boolean => {
  return element?.type === 'text';
};

const clearTextCacheForDesign = (designId: any) => {
  try {
    const id = designId || 'unsaved';
    try { localStorage.removeItem(`design_tool.personal_form_${id}_front`); } catch { }
    try { localStorage.removeItem(`design_tool.personal_form_${id}_back`); } catch { }
    try { localStorage.removeItem(`design_tool.personal_form_${id}`); } catch { }
  } catch { }
};

const normalizeEsqFieldKey = (raw: any): 'firstname' | 'lastname' | 'dob' | 'dod' | 'verse' | 'verse1' | 'verse2' | 'verse3' | 'other' => {
  const name = String(raw || '');
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (['firstname', 'first', 'fname', 'fullname', 'name', 'fnamemname', 'name1', 'primaryname', 'first_name', 'first-name'].includes(key)) return 'firstname';
  if (['lastname', 'last', 'lname', 'surname', 'familyname', 'family', 'name2', 'secondname', 'lname2', 'fname2', 'surnamealt', 'lastnamemodern', 'middle', 'middlename', 'mname', 'last_name', 'last-name', 'surname_name'].includes(key)) return 'lastname';
  if (['dob', 'birthdate', 'dateofbirth', 'born', 'birth_date', 'birth-date'].includes(key)) return 'dob';
  if (['dod', 'deathdate', 'dateofdeath', 'died', 'departed', 'death_date', 'death-date'].includes(key)) return 'dod';
  if (key === 'verse1' || key === 'verse_1' || key === 'verse-1') return 'verse1';
  if (key === 'verse2' || key === 'verse_2' || key === 'verse-2') return 'verse2';
  if (key === 'verse3' || key === 'verse_3' || key === 'verse-3') return 'verse3';
  if (['verse', 'epitaph', 'message', 'quote', 'verse_text', 'verse-text'].includes(key)) return 'verse';

  // Substring fallbacks for very weirdly named fields
  const lc = name.toLowerCase();
  if (lc.includes('first') && lc.includes('name')) return 'firstname';
  if (lc.includes('last') && lc.includes('name')) return 'lastname';
  if (lc.includes('family') && lc.includes('name')) return 'lastname';
  if (lc.includes('birth') && lc.includes('date')) return 'dob';
  if (lc.includes('death') && lc.includes('date')) return 'dod';
  if (lc.includes('verse2')) return 'verse2';
  if (lc.includes('verse3')) return 'verse3';

  return 'other';
};

interface LeftPanelProps {
  lockedMode?: 'LSP/QD' | 'ESQ';
}

const ESQ_TEXT_CACHE_KEY = 'design_tool.esq_text_form';
const DEFAULT_ESQ_TEXT_VALUES: EsqTextFormValues = {
  fullName: '',
  firstName: '',
  lastName: '',
  dob: '',
  dod: '',
  verse: '',
  verse2: '',
  verse3: '',
  abbreviateMonth: true,
  yearOnly: false,
  hideDeathDate: false,
};

const mapEsqDbFontToLoadedFamily = (rawFont: unknown, fieldName?: string, isPjb: boolean = false): string => {
  const fnLc = String(fieldName || '').toLowerCase();
  if (fnLc === 'verse' || fnLc.startsWith('verse')) {
    return isPjb ? 'MonotypeCorsiva' : 'Vermarco';
  }
  const value = String(rawFont || '').trim().toLowerCase();
  if (!value || value === 'custom') return 'MLCModifiedRomanSKS';
  if (value.includes('monument mod. roman')) return 'MLCModifiedRomanSKS';
  if (value.includes('pamega')) return isPjb ? 'PamegaScript' : 'Vermarco';
  if (value.includes('vermarco')) return 'Vermarco';
  if (value.includes('monotype') || value.includes('corsiva')) return isPjb ? 'MonotypeCorsiva' : 'Vermarco';
  return String(rawFont || 'MLCModifiedRomanSKS');
};

const clampByCharacterLimit = (value: string, limit?: number | null): string => {
  return String(value ?? '');
};

const clampNumber = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const forceAbbreviatedMonthDateString = (value: string): string => {
  const raw = String(value || '').toUpperCase();
  if (!raw.trim()) return raw;
  const map: Record<string, string> = {
    JANUARY: 'JAN.',
    FEBRUARY: 'FEB.',
    MARCH: 'MAR.',
    APRIL: 'APR.',
    MAY: 'MAY',
    JUNE: 'JUNE',
    JULY: 'JULY',
    AUGUST: 'AUG.',
    SEPTEMBER: 'SEPT.',
    OCTOBER: 'OCT.',
    NOVEMBER: 'NOV.',
    DECEMBER: 'DEC.',
    'JAN.': 'JAN.',
    'FEB.': 'FEB.',
    'MAR.': 'MAR.',
    'APR.': 'APR.',
    'AUG.': 'AUG.',
    'SEPT.': 'SEPT.',
    'OCT.': 'OCT.',
    'NOV.': 'NOV.',
    'DEC.': 'DEC.',
  };
  const match = raw.match(/^([A-Z]+\.?)\s+/);
  if (!match) return raw;
  const token = match[1];
  const key = token.endsWith('.') ? token : token;
  const replacement = map[key] || map[key.replace(/\.$/, '')];
  if (!replacement) return raw;
  return raw.replace(token, replacement);
};

// Main Component
const LeftPanel: React.FC<LeftPanelProps> = ({ lockedMode }) => {
  const {
    currentDesign,
    isTemplateSelected,
    setSelectedGraniteImage,
    setSelectedGraniteMeta,
    selectedGraniteMeta,
    incrementTemplateResetKey,
    updateDesign,
    setIsTemplateSelected,
    stageRef,
    selectedGraniteImage,
    setSelectedGraniteBaseImage,
    isApplyingDesign,
    setIsApplyingDesign,
    setTemplateElement,
    setCurrentTool,
    layoutGuides,
    setActivePlacementSlot,
    placementSlots,
    pixelsPerInch,
    renderedTemplateOrigin,
    openModal,
    setOpenModal,
    setShowPersonalTextOnCanvas,
    loadingMessage,
    setLoadingMessage,
    setIsGlobalLoading,
    loadTextAssets,
    textStyles,
    isLoadingTextStyles,
    loadArtworkCategories,
    loadShapesData,
    sharedColors,
    sharedModels,
    fetchModelDetails,
    fetchDesignPreview,
    setSelectedBackground,
    selectedBackground,
    sampleGuideLines,
    setSampleGuideLines,
    designMode,
    setDesignMode,
    setActiveSide,
    authedUser,
  } = useDesignContext();

  const selectedColorId = currentDesign?.selectedColorId;
  const selectedColorIndex = currentDesign?.selectedColorIndex;
  const selectedColorName = currentDesign?.selectedColorName;
  const isEditorUser = useMemo(() => authedUser?.is_editor === 'yes', [authedUser]);
  const effectiveDesignMode = designMode;

  // Re-apply color-specific LSP/QD text styles immediately after color switch,
  // so users don't need to open the Text panel to trigger style sync.
  useEffect(() => {
    if (designMode === 'ESQ') return;
    if (!textStyles || isLoadingTextStyles) return;

    const activeColorId = Number(selectedColorId || 0);
    const activeColorObj = sharedColors.find((c: any) => Number(c?.id) === activeColorId) || {};
    const isPJB = String((activeColorObj as any)?.code || '').toUpperCase() === 'PJB'
      || String((activeColorObj as any)?.name || '').toUpperCase().includes('BLACK')
      || activeColorId === 5
      || activeColorId === 14;
    const textColor = isPJB ? '#FFFFFF' : '#000000';

    const resolveFieldType = (id: string): 'first_name' | 'middle_name' | 'last_name' | 'date' | 'verse' | 'additional_text' | null => {
      const baseId = String(id || '').replace(/-back$/, '');
      if (
        baseId === 'firstName' ||
        baseId === 'p2_firstName'
      ) return 'first_name';
      if (
        baseId === 'middleName' ||
        baseId === 'p2_middleName'
      ) return 'middle_name';
      if (baseId === 'lastName') return 'last_name';
      if (
        baseId === 'dob' ||
        baseId === 'dod' ||
        baseId === 'p2_dob' ||
        baseId === 'p2_dod' ||
        baseId === 'weddingDate'
      ) return 'date';
      if (baseId === 'verse') return 'verse';
      if (baseId === 'additionalText' || /^additionalText\d+$/.test(baseId)) return 'additional_text';
      return null;
    };

    updateDesign(prev => {
      let changed = false;
      const nextElements = (prev.elements || []).map((el: any) => {
        if (el?.type !== 'text') return el;
        if (el?.properties?.role === 'esq_fixed_text') return el;

        const fieldType = resolveFieldType(el.id);
        if (!fieldType) return el;

        const style = (textStyles as any)?.[fieldType];
        const isManualFont = !!el?.properties?.isManualFont;
        const selectedColorObj = (sharedColors || []).find((c: any) => Number(c.id) === Number(selectedColorId));
        const isGrey = Boolean(
          selectedColorObj?.code === 'MG' ||
          selectedColorObj?.code === 'Grey' ||
          String(selectedColorObj?.name).toUpperCase().includes('GRAY') ||
          String(selectedColorObj?.name).toUpperCase().includes('GREY')
        );
        const nextFontFamily = (isManualFont && el?.properties?.fontFamily)
          ? el.properties.fontFamily
          : (String(el.id || '').includes('weddingDate')
              ? (isGrey ? 'Vermarco' : 'PamegaScript')
              : (style?.font_family || ((fieldType === 'verse' || fieldType === 'additional_text') ? 'MonotypeCorsiva' : el?.properties?.fontFamily)));
        const prevFill = String(el?.properties?.fill || '').toLowerCase();
        const nextFill = textColor.toLowerCase();

        if (nextFontFamily === el?.properties?.fontFamily && prevFill === nextFill) {
          return el;
        }

        changed = true;
        return {
          ...el,
          properties: {
            ...el.properties,
            ...(nextFontFamily ? { fontFamily: nextFontFamily } : {}),
            fill: textColor
          }
        };
      });

      return changed ? { ...prev, elements: nextElements } : prev;
    });
  }, [designMode, textStyles, isLoadingTextStyles, selectedColorId, sharedColors, updateDesign]);

  // Keep Art panel "granite type" in sync with the selected color.
  // This prevents showing both granite options when the chosen color already implies one.
  useEffect(() => {
    const granite = getGraniteForColor(selectedColorId ?? null, selectedColorName);
    setForcedGranite(granite ? granite : null);
  }, [selectedColorId, selectedColorName]);

  // Ensure color-specific text styles are refreshed immediately on color switches,
  // even when the Text panel is closed.
  useEffect(() => {
    if (!selectedColorId) return;
    Promise.resolve(loadTextAssets()).catch(() => { });
  }, [selectedColorId, currentDesign?.selectedSizeId, loadTextAssets]);



  const [selectedEsqModal, setSelectedEsqModalState] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('design_tool.esq_modal');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [selectedEsqTemplate, setSelectedEsqTemplateState] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('design_tool.esq_template');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [esqTextValues, setEsqTextValues] = useState<EsqTextFormValues>(() => {
    try {
      const saved = localStorage.getItem(ESQ_TEXT_CACHE_KEY);
      if (!saved) return DEFAULT_ESQ_TEXT_VALUES;
      const parsed = JSON.parse(saved);
      return {
        fullName: String(parsed?.fullName || ''),
        firstName: String(parsed?.firstName || ''),
        lastName: String(parsed?.lastName || ''),
        dob: String(parsed?.dob || ''),
        dod: String(parsed?.dod || ''),
        verse: String(parsed?.verse || ''),
        verse2: String(parsed?.verse2 || ''),
        verse3: String(parsed?.verse3 || ''),
        abbreviateMonth: parsed?.abbreviateMonth !== false,
        yearOnly: parsed?.yearOnly === true,
        hideDeathDate: parsed?.hideDeathDate === true,
      };
    } catch {
      return DEFAULT_ESQ_TEXT_VALUES;
    }
  });

  const isEsqSharedNameLimitTemplate = useMemo(() => {
    const name = String(selectedEsqTemplate?.name || '').trim().toLowerCase();
    return name.includes('bottom rose') || name.includes('book panel');
  }, [selectedEsqTemplate?.name]);

  const isEsqSerptopModal = useMemo(() => {
    const cat = String(selectedEsqModal?.category || '').toLowerCase();
    const name = String(selectedEsqModal?.name || '').toLowerCase();
    const tplName = String(selectedEsqTemplate?.name || '').toLowerCase();
    const tplEl = (currentDesign?.elements || []).find((el: any) =>
      el?.properties?.role === 'template_design' || el?.properties?.role === 'template'
    );
    const tplElModalName = String(tplEl?.properties?.esqModalName || '').toLowerCase();
    const tplElModalCat = String(tplEl?.properties?.esqModalCategory || '').toLowerCase();
    return (
      cat.includes('serptop') ||
      name.includes('serptop') ||
      tplName.includes('serptop') ||
      tplElModalName.includes('serptop') ||
      tplElModalCat.includes('serptop')
    );
  }, [selectedEsqModal, selectedEsqTemplate, currentDesign?.elements]);


  // Wrap setters to also persist to localStorage
  const setSelectedEsqModal = useCallback((modal: any) => {
    setSelectedEsqModalState(modal);
    try {
      if (modal) localStorage.setItem('design_tool.esq_modal', JSON.stringify(modal));
      else localStorage.removeItem('design_tool.esq_modal');
    } catch { }
  }, []);

  const setSelectedEsqTemplate = useCallback((tpl: any) => {
    setSelectedEsqTemplateState(tpl);
    try {
      if (tpl) localStorage.setItem('design_tool.esq_template', JSON.stringify(tpl));
      else localStorage.removeItem('design_tool.esq_template');
    } catch { }
  }, []);

  const normalizeEsqFieldContent = useCallback((field: string, content: string, defaultContent?: string): string => {
    const rawValue = String(content || '');
    const trimmed = rawValue.trim();
    const fieldLc = field.toLowerCase();
    const defTrim = String(defaultContent || '').trim();
    if (fieldLc === 'firstname') {
      if (trimmed === 'FirstName' || (defTrim && trimmed === defTrim)) return '';
      return rawValue.toUpperCase();
    }
    if (fieldLc === 'lastname') {
      if (trimmed === 'LastName' || (defTrim && trimmed === defTrim)) return '';
      return adjustLastNameCasing(rawValue);
    }
    if (fieldLc === 'verse' || fieldLc.startsWith('verse')) {
      // Verse should preserve user-entered casing (upper/lower/mixed).
      // Keep spacing exactly as typed; only treat placeholder as empty.
      if (trimmed === 'Select a Verse' || (defTrim && trimmed === defTrim)) return '';
      return rawValue;
    }
    if (fieldLc === 'dob') {
      if (defTrim && trimmed === defTrim) return '';
      return trimmed === 'DOB' ? '' : rawValue.toUpperCase();
    }
    if (fieldLc === 'dod') {
      if (defTrim && trimmed === defTrim) return '';
      return trimmed === 'DOD' ? '' : rawValue.toUpperCase();
    }
    return rawValue.toUpperCase();
  }, []);

  const applyEsqTextToFixedFields = useCallback((values: EsqTextFormValues) => {
    if (isApplyingDesign || (window as any).__isApplyingDesign) return;
    updateDesign(prev => {
      const seenFields = new Set<string>();
      const filteredElements = (prev.elements || []).filter((el: any) => {
        if (el?.type === 'text' && el?.properties?.role === 'esq_fixed_text') {
          const fn = normalizeEsqFieldKey(el?.properties?.fieldName);
          if (!isEsqSerptopModal && (fn === 'verse2' || fn === 'verse3')) {
            return false;
          }
          if (fn === 'verse' || fn === 'verse1' || fn === 'verse2' || fn === 'verse3') {
            if (seenFields.has(fn)) return false;
            seenFields.add(fn);
          }
        }
        return true;
      });

      const nextElements = filteredElements.map((el: any) => {
        if (el?.type !== 'text' || el?.properties?.role !== 'esq_fixed_text') return el;
        if (el?.properties?.esqTextReadOnly) return el;
        const field = normalizeEsqFieldKey(el?.properties?.fieldName);
        const charLimit = Number(el?.properties?.characterLimit || 0) || undefined;
        const dbDefault = String(el?.properties?.defaultContent || '').trim();
        let nextContent = el?.properties?.content;
        const combined = `${String(values.firstName || '').trim()} ${String(values.lastName || '').trim()}`.trim();
        const mergedFullName = isEsqSharedNameLimitTemplate
          ? (String(values.fullName || '') || combined)
          : combined;

        const hasLastNameField = (prev.elements || []).some((e: any) =>
          e?.type === 'text' &&
          normalizeEsqFieldKey(e?.properties?.fieldName) === 'lastname'
        );

        if (field === 'firstname') {
          nextContent = !hasLastNameField
            ? mergedFullName
            : values.firstName;
        } else if (field === 'lastname') {
          nextContent = values.lastName;
        } else if (field === 'dob') {
          nextContent = values.dob;
        } else if (field === 'dod') {
          nextContent = values.hideDeathDate ? '' : values.dod;
        } else if (field === 'verse' || field === 'verse1') {
          nextContent = values.verse;
        } else if (field === 'verse2') {
          nextContent = isEsqSerptopModal ? values.verse2 : '';
        } else if (field === 'verse3') {
          nextContent = isEsqSerptopModal ? values.verse3 : '';
        }
        nextContent = clampByCharacterLimit(String(nextContent || ''), charLimit);
        if (String(nextContent) === String(el?.properties?.content || '')) return el;
        return {
          ...el,
          properties: {
            ...el.properties,
            content: String(nextContent),
          }
        };
      });

      if (isEsqSerptopModal) {
        const hasVerse2 = nextElements.some((e: any) => normalizeEsqFieldKey(e?.properties?.fieldName) === 'verse2');
        const hasVerse3 = nextElements.some((e: any) => normalizeEsqFieldKey(e?.properties?.fieldName) === 'verse3');
        const verse1El = nextElements.find((e: any) => ['verse', 'verse1'].includes(normalizeEsqFieldKey(e?.properties?.fieldName)));

        if (!hasVerse2 && verse1El && values.verse2 && String(values.verse2).trim() !== '') {
          nextElements.push({
            id: `esq-txt-Verse2-${Date.now()}`,
            type: 'text' as const,
            side: 'front' as const,
            x: Number(verse1El.x),
            y: Number(verse1El.y) + 35,
            width: Number(verse1El.width),
            height: Number(verse1El.height),
            rotation: 0,
            properties: {
              ...verse1El.properties,
              content: values.verse2,
              fieldName: 'Verse2',
              esqBounds: verse1El.properties?.esqBounds ? {
                ...verse1El.properties.esqBounds,
                h1: Number(verse1El.properties.esqBounds.h1) + 0.85,
                h2: Number(verse1El.properties.esqBounds.h2) + 0.85,
              } : undefined
            }
          });
        }

        if (!hasVerse3 && verse1El && values.verse3 && String(values.verse3).trim() !== '') {
          nextElements.push({
            id: `esq-txt-Verse3-${Date.now()}`,
            type: 'text' as const,
            side: 'front' as const,
            x: Number(verse1El.x),
            y: Number(verse1El.y) + 70,
            width: Number(verse1El.width),
            height: Number(verse1El.height),
            rotation: 0,
            properties: {
              ...verse1El.properties,
              content: values.verse3,
              fieldName: 'Verse3',
              esqBounds: verse1El.properties?.esqBounds ? {
                ...verse1El.properties.esqBounds,
                h1: Number(verse1El.properties.esqBounds.h1) + 1.70,
                h2: Number(verse1El.properties.esqBounds.h2) + 1.70,
              } : undefined
            }
          });
        }
      }

      return { ...prev, elements: nextElements };
    });
  }, [updateDesign, isEsqSharedNameLimitTemplate, isEsqSerptopModal]);

  // Bottom Rose / Book Panel should always abbreviate months (JAN. vs JANUARY).
  useEffect(() => {
    if (!isEsqSharedNameLimitTemplate) return;
    const nextDob = esqTextValues.dob ? forceAbbreviatedMonthDateString(esqTextValues.dob) : '';
    const nextDod = esqTextValues.dod ? forceAbbreviatedMonthDateString(esqTextValues.dod) : '';
    const needsUpdate =
      esqTextValues.abbreviateMonth === false ||
      String(nextDob) !== String(esqTextValues.dob || '') ||
      String(nextDod) !== String(esqTextValues.dod || '');
    if (!needsUpdate) return;
    const next: EsqTextFormValues = {
      ...esqTextValues,
      abbreviateMonth: true,
      dob: nextDob,
      dod: nextDod,
    };
    setEsqTextValues(next);
    try { localStorage.setItem(ESQ_TEXT_CACHE_KEY, JSON.stringify(next)); } catch { }
    applyEsqTextToFixedFields(next);
  }, [isEsqSharedNameLimitTemplate, esqTextValues, applyEsqTextToFixedFields]);

  const handleEsqTextChange = useCallback((next: EsqTextFormValues) => {
    const fieldLimits: Record<string, number | undefined> = {};
    for (const el of currentDesign?.elements || []) {
      if (el?.type !== 'text' || el?.properties?.role !== 'esq_fixed_text') continue;
      const fieldKey = normalizeEsqFieldKey(el?.properties?.fieldName);
      const lim = Number(el?.properties?.characterLimit || 0);
      if (lim > 0) fieldLimits[fieldKey] = lim;
    }

    const prev = esqTextValues;
    let nextSync = { ...next };

    const nextFull = adjustLastNameCasing(nextSync.fullName || '');
    const prevFull = adjustLastNameCasing(prev.fullName || '');
    let nextFirst = String(nextSync.firstName || '').toUpperCase();
    const prevFirst = String(prev.firstName || '').toUpperCase();
    let nextLast = adjustLastNameCasing(nextSync.lastName || '');
    const prevLast = adjustLastNameCasing(prev.lastName || '');

    if (isEsqSharedNameLimitTemplate) {
      // Shared limit logic for single-row templates (Book Panel / Bottom Rose)
      const nameLimit = fieldLimits.firstname || fieldLimits.lastname || 20;

      if (nextFirst !== prevFirst) {
        // First name changed: clamp based on current last name
        const space = (nextFirst && nextLast) ? 1 : 0;
        const available = Math.max(0, nameLimit - (nextLast ? nextLast.length + space : 0));
        if (nextFirst.length > available) {
          nextFirst = nextFirst.slice(0, available);
        }
      } else if (nextLast !== prevLast) {
        // Last name changed: clamp based on current first name
        const space = (nextFirst && nextLast) ? 1 : 0;
        const available = Math.max(0, nameLimit - (nextFirst ? nextFirst.length + space : 0));
        if (nextLast.length > available) {
          nextLast = nextLast.slice(0, available);
        }
      }

      nextSync.firstName = nextFirst;
      nextSync.lastName = nextLast;
      nextSync.fullName = `${nextFirst} ${nextLast}`.trim();
    } else {
      // Standard ESQ logic: Auto-sync Full Name vs First/Last Name
      if (nextFull !== prevFull) {
        const parts = nextFull.trim().split(/\s+/);
        if (parts.length > 1) {
          nextSync.lastName = parts.pop() || '';
          nextSync.firstName = parts.join(' ');
        } else {
          nextSync.firstName = nextFull;
          nextSync.lastName = '';
        }
      } else if (nextFirst !== prevFirst || nextLast !== prevLast) {
        nextSync.fullName = `${nextFirst} ${nextLast}`;
      }
    }

    const normalized: EsqTextFormValues = {
      fullName: clampByCharacterLimit(adjustLastNameCasing(nextSync.fullName || ''), fieldLimits.firstname || fieldLimits.lastname),
      firstName: clampByCharacterLimit(String(nextSync.firstName || '').toUpperCase(), fieldLimits.firstname),
      lastName: clampByCharacterLimit(adjustLastNameCasing(nextSync.lastName || ''), fieldLimits.lastname),
      dob: String(nextSync.dob || '').toUpperCase(),
      dod: String(nextSync.dod || '').toUpperCase(),
      // Keep verse spacing + casing exactly as typed.
      verse: clampByCharacterLimit(String(nextSync.verse || ''), fieldLimits.verse),
      verse2: clampByCharacterLimit(String(nextSync.verse2 || ''), fieldLimits.verse2 || fieldLimits.verse),
      verse3: clampByCharacterLimit(String(nextSync.verse3 || ''), fieldLimits.verse3 || fieldLimits.verse),
      abbreviateMonth: nextSync.abbreviateMonth !== false,
      yearOnly: nextSync.yearOnly === true,
      hideDeathDate: nextSync.hideDeathDate === true,
    };
    setEsqTextValues(normalized);
    try { localStorage.setItem(ESQ_TEXT_CACHE_KEY, JSON.stringify(normalized)); } catch { }
    applyEsqTextToFixedFields(normalized);
  }, [applyEsqTextToFixedFields, currentDesign?.elements, esqTextValues]);


  // Guard against race conditions when switching colors rapidly
  const colorPreviewReqIdRef = React.useRef<number>(0);
  const esqSwitchReqIdRef = React.useRef<number>(0);

  // State - declare before effects that use them
  const [colors, setColors] = useState<any[]>([]);
  const [isArtImageGridActive, setArtImageGridActive] = useState(false);
  const [forcedGranite, setForcedGranite] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [vasesDisabled, setVasesDisabled] = useState(false);
  const [isApplyingShape, setIsApplyingShape] = useState(false);
  const [cachedModels, setCachedModels] = useState<any[]>([]);
  const preloadedModelThumbsRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageUploadRef = useRef<HTMLInputElement>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Cache verses and categories in LeftPanel to avoid re-fetching and "flash" issues
  const [cachedVerses, setCachedVerses] = useState<any[]>([]);
  const [cachedCategories, setCachedCategories] = useState<string[]>([]);
  const [areVersesLoaded, setAreVersesLoaded] = useState(false);
  const [hasOpenedShapes, setHasOpenedShapes] = useState(false);

  // 1. Memoized values - move up so handlers can use them
  const currentModelId = useMemo(() =>
    selectedGraniteMeta?.modelId || (currentDesign as any)?.selectedModelId,
    [selectedGraniteMeta, currentDesign]
  );

  const selectedModel = useMemo(() => {
    return sharedModels?.find((m: any) => m.id === currentModelId);
  }, [sharedModels, currentModelId]);

  const isFlat20x10x3 = useMemo(() => {
    return (
      (selectedModel?.model_no === 'MF01' || selectedModel?.name?.toLowerCase().includes('flat')) &&
      currentDesign?.selectedSizeName === '20x10x3'
    );
  }, [selectedModel, currentDesign?.selectedSizeName]);

  const porcelainDisabled = currentDesign?.activeSide === 'back' || isFlat20x10x3;
  const porcelainTitle = currentDesign?.activeSide === 'back'
    ? "Porcelain photos are only allowed on the front side."
    : (isFlat20x10x3 ? "Porcelain is not available for this size." : "Upload Photo");

  // Auto-remove porcelain if switching to the flat marker 20x10x3 size
  useEffect(() => {
    if (isFlat20x10x3 && currentDesign?.elements) {
      const hasPorcelain = currentDesign.elements.some(el => el.properties?.role === 'porcelain');
      if (hasPorcelain && updateDesign) {
        updateDesign(prev => ({
          ...prev,
          elements: prev.elements.filter(el => el.properties?.role !== 'porcelain')
        }));
      }
    }
  }, [isFlat20x10x3, currentDesign?.elements, updateDesign]);

  const designElements = useMemo(() =>
    currentDesign?.elements || [],
    [currentDesign]
  );

  // Keep local selected color in sync with design state when a saved design is loaded.
  // Important: do NOT regenerate the template while a saved design is being applied
  // (Edit/Open), or if a template element already exists in the loaded design.
  // 0. Helpers
  // 1. Core Handlers
  const handleVaseSelect = useCallback(async (vase: any, config?: {
    preferredVariant?: string,
    sizeId?: number,
    modelId?: number,
    colorId?: number,
    cachedPreviewData?: any,
    cachedDetailData?: any
  }) => {
    try { setIsApplyingDesign && setIsApplyingDesign(true); } catch { }
    setOpenModal(null);
    const src = vase?.thumb_path
      ? buildApiUrl(`/api/uploads/${vase.thumb_path.replace(/^uploads[\\/]/, '')}`)
      : vase?.image_path
        ? buildApiUrl(`/api/uploads/${vase.image_path.replace(/^uploads[\\/]/, '')}`)
        : '';

    const vaseIdBase = Date.now();
    const colorId = config?.colorId ?? selectedColorId ?? (currentDesign as any)?.selectedColorId ?? selectedGraniteMeta?.colorId ?? null;

    const preferredVariant = config?.preferredVariant;
    const isAV02 = preferredVariant === 'AV02' ||
      String(vase.vase_id || '').toUpperCase() === 'AV02' ||
      String(vase.name || '').toLowerCase().includes('av02');

    const determinedVariant = isAV02 ? 'AV02' : 'AV01';

    updateDesign(prev => {
      const template = prev.elements.find(isTemplateElement);
      const elementsWithoutVases = prev.elements.filter(el =>
        !(el.type === 'artwork' && el.properties?.role === 'vase')
      );

      // Require both a template element and a valid pixelsPerInch — no fallbacks.
      // VaseElement always recomputes size and position from compositeProps.base,
      // so the stored x/y/width/height here are just placeholders until first render.
      if (!template || !pixelsPerInch || pixelsPerInch <= 0) return { ...prev };

      // Match LSP/QD vase sizing behavior: size relative to stone height (not PPI).
      // This keeps ESQ and LSP/QD consistent and avoids PPI-driven drift.
      const heightFactor = isAV02 ? 0.39 : 0.42;
      const ratio = isAV02 ? (2.6 / 4) : (1.8 / 4);
      const overlap = isAV02 ? 1 : 5;
      const estH = Math.max(20, Math.round(Number(template.height || 0) * heightFactor));
      const estW = Math.max(20, Math.round(estH * ratio));

      const vaseProperties = {
        src,
        vaseSrcOriginal: src,
        colorId,
        name: vase.name,
        vaseId: vase.id,
        vaseVariantId: determinedVariant,
        draggable: false,
        role: 'vase',
        type: 'Vase',
        transformable: false,
        rotationLocked: true,
        side: 'left'
      };

      const leftVase = {
        id: `vase-left-${vaseIdBase}`,
        type: 'artwork' as const,
        x: template.x,
        y: template.y + template.height - estH + overlap,
        width: estW, height: estH, rotation: 0,
        properties: vaseProperties
      };

      const rightVase = {
        id: `vase-right-${vaseIdBase}`,
        type: 'artwork' as const,
        x: template.x + template.width - estW,
        y: template.y + template.height - estH + overlap,
        width: estW, height: estH, rotation: 0,
        properties: {
          ...vaseProperties,
          side: 'right'
        }
      };

      return {
        ...prev,
        elements: [...elementsWithoutVases, leftVase, rightVase]
      };
    });

    try {
      const targetModelId = config?.modelId ?? selectedGraniteMeta?.modelId;
      const targetSizeId = config?.sizeId ?? selectedGraniteMeta?.sizeId;
      const targetColorId = config?.colorId ?? selectedGraniteMeta?.colorId;

      if (targetModelId && targetSizeId && targetColorId) {
        let previewData = config?.cachedPreviewData;
        let detailData = config?.cachedDetailData;

        if (!previewData || !detailData) {
          const [pData, dData] = await Promise.all([
            !previewData ? fetchDesignPreview(Number(targetModelId), Number(targetSizeId), Number(targetColorId)) : Promise.resolve(previewData),
            !detailData ? fetchModelDetails(Number(targetModelId), Number(targetColorId)) : Promise.resolve(detailData)
          ]);
          previewData = pData;
          detailData = dData;
        }

        if (previewData && detailData) {
          const modelConfig = previewData.data?.config_json
            ? (typeof previewData.data.config_json === 'string' ? JSON.parse(previewData.data.config_json) : previewData.data.config_json)
            : null;

          const sizes = Array.isArray(detailData?.sizes) ? detailData.sizes : [];
          const sizeMatch = sizes.find((s: any) => Number(s.id) === Number(targetSizeId));
          const sizeLabel = (sizeMatch?.die_size || sizeMatch?.base_size || sizeMatch?.size || '').toString();
          const digits = sizeLabel.split('/')[0].match(/\d+/g) || [];
          const nums = digits.map(Number);
          const isSingleVaseSize = ((nums.length >= 3 && [16, 4, 18].every(v => nums.includes(v))) || (nums.length >= 3 && [18, 4, 24].every(n => nums.includes(n))));
          if (isSingleVaseSize && modelConfig?.vases?.[0]?.pos !== 'both') {
            updateDesign(prev => ({ ...prev, elements: prev.elements.filter(el => el.id !== `vase-left-${vaseIdBase}`) }));
          }
        }
      }
    } catch (err) { } finally { try { setIsApplyingDesign(false); } catch { } }
  }, [selectedColorId, selectedGraniteMeta, updateDesign, setIsApplyingDesign, setLoadingMessage, currentDesign, fetchModelDetails, fetchDesignPreview, pixelsPerInch]);

  const checkVasesAllowed = useCallback(async (modelId: number, sizeId?: number, colorId?: number | null): Promise<boolean> => {
    if (!modelId) return false;
    try {
      // Primary check: use vase_image from the designs table (most accurate).
      // fetchDesignPreview is cached so this is cheap on repeat calls.
      if (sizeId && colorId) {
        const previewData = await fetchDesignPreview(modelId, sizeId, colorId);
        const vaseImg = previewData?.data?.vase_image;
        return typeof vaseImg === 'string' && vaseImg.trim().length > 0;
      }
      return false;
    } catch (error) { return false; }
  }, [fetchDesignPreview]);

  const applyDefaultVase = useCallback(async (colorId: number, modelId: number, sizeId: number, overrideSizeLabel?: string) => {
    try {
      setLoadingMessage('Configuring vases...');
      // fetchDesignPreview is cached; use it to check vase_image and get all needed data at once.
      let previewData = await fetchDesignPreview(modelId, sizeId, colorId);
      const vaseImg = previewData?.data?.vase_image;
      if (!vaseImg || typeof vaseImg !== 'string' || !vaseImg.trim()) return; // No vase for this design
      let detailData = await fetchModelDetails(modelId, colorId);
      if (!previewData || !detailData) return;
      // Derive variant from the image path (Av02 → AV02, anything else → AV01).
      // The designs table has no separate vase_variant column.
      const vaseVariant = /[Aa]v02/.test(vaseImg) ? 'AV02' : 'AV01';
      const matchingVase = { id: 0, name: vaseVariant, vase_id: vaseVariant, image_path: vaseImg, thumb_path: vaseImg };
      await handleVaseSelect(matchingVase, { preferredVariant: vaseVariant, modelId: Number(modelId), sizeId: Number(sizeId), colorId: Number(colorId), cachedPreviewData: previewData, cachedDetailData: detailData });
    } catch (err) { } finally { setLoadingMessage(''); }
  }, [handleVaseSelect, fetchDesignPreview, fetchModelDetails, setLoadingMessage]);

  const checkCompatibilityIssues = useCallback(async (
    targetColorId: number,
    currentElements: any[]
  ): Promise<{ needsVaseRemoval: boolean; needsArtRemoval: boolean; needsTextRemoval: boolean; willDisallowVases: boolean; needsSizeChange?: boolean }> => {
    let willDisallowVases = false;
    if (!currentModelId) {
      willDisallowVases = true;
    } else {
      try {
        const sizeId = Number(selectedGraniteMeta?.sizeId ?? (currentDesign as any)?.selectedSizeId ?? 0);
        const allowed = await checkVasesAllowed(currentModelId, sizeId || undefined, targetColorId);
        willDisallowVases = !allowed;
      } catch (err) {
        willDisallowVases = true;
      }
    }
    const hasVases = currentElements.some(isVaseElement);
    const hasText = currentElements.some(isTextElement);
    let needsSizeChange = false;
    if (!isApplyingShape && currentModelId) {
      try {
        const sizeToCheck = Number(selectedGraniteMeta?.sizeId ?? (currentDesign as any)?.selectedSizeId ?? 0);
        if (sizeToCheck && sizeToCheck > 0) {
          try {
            const details = await fetchModelDetails(currentModelId, targetColorId);
            const sizes = Array.isArray(details?.sizes) ? details.sizes : [];
            const found = sizes.some((s: any) => Number(s.id) === sizeToCheck);
            if (!found) needsSizeChange = true;
          } catch (err) { }
        }
      } catch (err) { }
    }
    return {
      needsVaseRemoval: hasVases && willDisallowVases,
      needsArtRemoval: false,
      needsTextRemoval: hasText,
      willDisallowVases,
      needsSizeChange
    };
  }, [currentModelId, selectedGraniteMeta, currentDesign, checkVasesAllowed, isApplyingShape, fetchModelDetails]);

  // Avoid declaration-order callback dependency issues between ESQ handlers.
  const handleEsqTemplateSelectRef = useRef<(template: any) => Promise<void>>(async () => { });

  const handleEsqModalSelect = useCallback(async (modal: any, incomingReqId?: number | { retainTemplate?: boolean; keepColorId?: boolean }, explicitColorId?: number) => {
    if (isApplyingDesign || (window as any).__isApplyingDesign) return;
    const reqId = typeof incomingReqId === 'number' ? incomingReqId : ++esqSwitchReqIdRef.current;
    const isStale = () => reqId !== esqSwitchReqIdRef.current;
    const previousTemplate = selectedEsqTemplate;
    const hadAppliedEsqTemplate = (currentDesign?.elements || []).some((el: any) => {
      const role = String(el?.properties?.role || '').toLowerCase();
      return role === 'template_design' || role === 'esq_fixed_text' || role === 'esq_fixed_artwork';
    });

    setSelectedEsqModal(modal);
    // Keep selected template state so color switches can re-apply equivalent template.
    setOpenModal(null);
    try { setActivePlacementSlot && setActivePlacementSlot(null); } catch { }

    const colorId = explicitColorId || Number(modal?.color_id || 0) || Number(selectedColorId || 0) || 5;
    const colorIndex = (sharedColors || []).findIndex(c => Number(c.id) === colorId);

    setIsLoading(true);
    setLoadingMessage('Initializing ESQ modal...');

    try {
      // For ESQ, we treat the modal as the base granite image
      const imgSrc = buildApiUrl('/api/uploads/' + modal.image_path);
      const baseSrc = modal.base_path ? buildApiUrl('/api/uploads/' + modal.base_path) : null;

      // When switching ESQ modal/color, clear old template/fixed layers AND transient
      // text nodes so previous-layout text cannot render on the new stone.
      // User-entered ESQ values are preserved in esqTextValues/localStorage and will
      // be re-applied when the ESQ template/positions are loaded.
      const nextElements = (currentDesign?.elements || []).filter((el: any) =>
        !isTemplateElement(el) &&
        !isEsqTemplateLayerElement(el) &&
        el?.type !== 'text'
      );

      const resolvedColorName = (sharedColors.find((c: any) => Number(c?.id) === Number(colorId)) as any)?.name
        || modal.color_name
        || ([5, 14].includes(Number(colorId)) ? 'Black' : 'Grey');

      updateDesign(prev => ({
        ...prev,
        selectedColorId: colorId,
        selectedColorIndex: colorIndex >= 0 ? colorIndex : 0,
        selectedColorName: resolvedColorName,
        selectedModelId: modal.id,
        selectedModelName: modal.name,
        selectedSizeId: 0, // ESQ uses fixed logical size
        elements: nextElements
      }));

      setSelectedGraniteImage?.(imgSrc);
      if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(baseSrc);

      setSelectedGraniteMeta({
        modelId: modal.id,
        sizeId: 0,
        colorId,
        src: imgSrc
      });

      // Still set template element for text field logic, but ensure it's treated as background role
      setTemplateElement({
        src: imgSrc,
        width: 1200,
        height: 600,
        colorId: colorId,
        isFlatMarker: modal.category === 'Flat Markers',
        extraProps: {
          designMode: 'ESQ',
          esqModalId: modal.id,
          esqModalName: modal.name,
          esqModalWidthIn: modal.width_in ?? modal.widthIn,
          esqModalHeightIn: modal.height_in ?? modal.heightIn,
          role: 'template'
        }
      });

      setIsTemplateSelected(true);

      // Ensure ESQ fixed text is visible on canvas after modal loads
      try { setShowPersonalTextOnCanvas(true); } catch { }

      // Apply ESQ modal background first; fallback to solid default color.
      const modalBgPath = String(modal.background_path || '').trim();
      if (setSelectedBackground) {
        const nextBg = modalBgPath
          ? {
            id: 1,
            name: `${modal.category || 'ESQ'} Background`,
            image_url: buildApiUrl('/api/uploads/' + modalBgPath.replace(/^\/?uploads\//, '')),
            background_color: '#E0E0E0',
          }
          : {
            id: 0,
            name: 'Default Color',
            image_url: null,
            background_color: '#E0E0E0',
          };
        setSelectedBackground(nextBg as any);
        try { localStorage.setItem('design_tool.background', JSON.stringify(nextBg)); } catch { }
      }

      // Auto-apply vases if applicable
      if (modal.vase_path) {
        const vaseVariant = /[Aa]v02/.test(modal.vase_path) ? 'AV02' : 'AV01';
        const matchingVase = { id: 0, name: vaseVariant, vase_id: vaseVariant, image_path: modal.vase_path, thumb_path: modal.vase_path };
        await handleVaseSelect(matchingVase, { preferredVariant: vaseVariant, modelId: modal.id, sizeId: 0, colorId: colorId });
      } else {
        await applyDefaultVase(colorId, modal.id, 0);
      }

      // ESQ retain behavior on modal change:
      // attempt to re-apply the previously selected template for the new modal/color.
      if (!isStale() && hadAppliedEsqTemplate && previousTemplate) {
        try {
          const tplRes = await fetch(buildApiUrl(`/api/esq/templates/${modal.id}/${colorId}`));
          if (tplRes.ok) {
            const tplList = await tplRes.json();
            const templates = Array.isArray(tplList) ? tplList : [];
            const prevTplId = Number((previousTemplate as any)?.id || 0);
            const prevTplName = String(
              (previousTemplate as any)?.name ||
              (previousTemplate as any)?.template_name ||
              ''
            ).trim().toLowerCase();

            const matchedTemplate =
              (prevTplName ? templates.find((t: any) => String(t?.name || t?.template_name || '').trim().toLowerCase() === prevTplName) : null) ||
              templates.find((t: any) => Number(t?.id || 0) === prevTplId) ||
              null;

            if (matchedTemplate && !isStale()) {
              await handleEsqTemplateSelectRef.current({
                ...matchedTemplate,
                modalId: modal.id,
                colorId
              });
            }
          }
        } catch {
          // Retain-template soft failure — non-critical
        }
      }

      if (isStale()) return;
    } catch (e) {
      console.error('Failed to select ESQ modal:', e);
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [sharedColors, setSelectedGraniteImage, setSelectedGraniteMeta, setTemplateElement, setIsTemplateSelected, setLoadingMessage, selectedColorId, setSelectedBackground, currentDesign?.elements, updateDesign, setSelectedGraniteBaseImage, handleVaseSelect, applyDefaultVase, setOpenModal, setIsLoading, selectedEsqTemplate]);

  const handleEsqTemplateSelect = useCallback(async (template: any) => {
    if (isApplyingDesign || (window as any).__isApplyingDesign) return;
    setOpenModal(null);

    setIsLoading(true);
    setLoadingMessage('Applying ESQ template...');
    // ESQ template apply should always show fixed text on-canvas
    try { setShowPersonalTextOnCanvas(true); } catch { }

    try {
      const modalId = template?.modalId || selectedEsqModal?.id;
      // Prioritize explicitly-passed colorId (from color switch) over stale selectedColorId closure
      const colorId = template?.colorId || template?.color_id || selectedColorId || selectedEsqModal?.color_id || 5;

      // Always re-fetch fresh template data from API so artwork_image_path is current
      let freshTemplate = template;
      try {
        const tplRes = await fetch(buildApiUrl(`/api/esq/templates/${modalId}/${colorId}`));
        if (tplRes.ok) {
          const tplList = await tplRes.json();
          const targetName = String(template?.name || template?.template_name || '').trim().toLowerCase();
          const found = Array.isArray(tplList)
            ? ((targetName ? tplList.find((t: any) => String(t?.name || t?.template_name || '').trim().toLowerCase() === targetName) : null) ||
              tplList.find((t: any) => Number(t.id) === Number(template.id)))
            : null;
          if (found) freshTemplate = found;
        }
      } catch { /* use original template if re-fetch fails */ }

      // Persist the fresh template (with artwork_image_path) to localStorage
      setSelectedEsqTemplate(freshTemplate);

      let positionsMap: Record<string, any> = {};
      try {
        const res = await fetch(buildApiUrl(`/api/esq/positions/${modalId}/${freshTemplate.id}/${colorId}`));
        if (res.ok) {
          const positions = await res.json();
          if (positions && typeof positions === 'object') positionsMap = positions;
        }
      } catch { }

      // Fallback: if positionsMap for target colorId is empty, fetch fallback positions
      if (Object.keys(positionsMap).length === 0) {
        try {
          const resFallback = await fetch(buildApiUrl(`/api/esq/positions/${modalId}/${freshTemplate.id}/5`));
          if (resFallback.ok) {
            const positionsFallback = await resFallback.json();
            if (positionsFallback && typeof positionsFallback === 'object') positionsMap = positionsFallback;
          }
        } catch { }
      }

      const esqDefaultFromRow = (row: any) => String(row?.default_content ?? '').trim();
      const normalizeFieldKey = (v: string) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const findPosition = (field: string) => {
        const direct = positionsMap[field];
        if (direct) return direct;
        const target = normalizeFieldKey(field);
        const foundKey = Object.keys(positionsMap).find((k) => normalizeFieldKey(k) === target);
        return foundKey ? positionsMap[foundKey] : null;
      };

      // Keep ESQ guide-line behavior aligned with LSP/QD:
      // do not auto-overwrite H1/H2/V1/V2 from template field bounds.

      updateDesign(prev => {
        const nextElements = (prev.elements || []).filter((el: any) =>
          el.properties?.role === 'template' || el.properties?.role === 'vase'
        );

        // Add template image on top of modal
        const tplImg = {
          id: `esq-tpl-${Date.now()}`,
          type: 'artwork' as const,
          side: 'front' as const,
          x: 0,
          y: 0,
          width: 1200,
          height: 600,
          rotation: 0,
          properties: {
            src: freshTemplate.artwork_image_path
              ? (findPosition('Artwork') ? '' : buildApiUrl('/api/uploads/' + freshTemplate.artwork_image_path))
              : '',
            role: 'template_design',
            templateId: freshTemplate.id,
            modalId,
            colorId,
            isFixedPosition: true,
            draggable: false
          }
        };

        // Add Fixed Text Fields (Separator = fixed glyph row, e.g. "-" between names; not user-editable)
        const isSerptop = (() => {
          const cat = String(selectedEsqModal?.category || '').toLowerCase();
          const name = String(selectedEsqModal?.name || '').toLowerCase();
          const tplName = String(freshTemplate?.name || '').toLowerCase();
          return cat.includes('serptop') || name.includes('serptop') || tplName.includes('serptop');
        })();

        const fields = isSerptop
          ? ['FirstName', 'Separator', 'LastName', 'DOB', 'DOD', 'Verse', 'Verse2', 'Verse3']
          : ['FirstName', 'Separator', 'LastName', 'DOB', 'DOD', 'Verse'];
        fields.forEach(field => {

          const isFullNameTpl = (() => {
            const tplName = String((freshTemplate as any)?.name || '').trim().toLowerCase();
            return tplName.includes('bottom rose') || tplName.includes('book panel');
          })();

          let p = findPosition(field);
          if (!p && (field === 'Verse2' || field === 'Verse3') && isSerptop) {
            const mainP = findPosition('Verse') || findPosition('Verse1');
            if (mainP) {
              const offset = field === 'Verse2' ? 35 : 70;
              const hOffset = field === 'Verse2' ? 0.85 : 1.70;
              p = {
                ...mainP,
                field_name: field,
                y: Number(mainP.y) + offset,
                h1: mainP.h1 !== null && mainP.h1 !== undefined ? Number(mainP.h1) + hOffset : null,
                h2: mainP.h2 !== null && mainP.h2 !== undefined ? Number(mainP.h2) + hOffset : null,
              };
            }
          }

          if (p) {
            const rowDefault = esqDefaultFromRow(p);
            const isSeparator = field === 'Separator';
            const hasGuideBounds = p.h1 !== null && p.h1 !== undefined
              && p.h2 !== null && p.h2 !== undefined
              && p.v1 !== null && p.v1 !== undefined
              && p.v2 !== null && p.v2 !== undefined;

            const combined = `${String(esqTextValues.firstName || '').trim()} ${String(esqTextValues.lastName || '').trim()}`.trim();
            const mergedFullName = isFullNameTpl
              ? (String(esqTextValues.fullName || '').trim() || combined)
              : combined;

            const hasLastNamePosition = !!findPosition('LastName');

            const slotContent = isSeparator
              ? (rowDefault || '-')
              : field === 'FirstName'
                ? ((isFullNameTpl && !hasLastNamePosition) ? mergedFullName : esqTextValues.firstName)
                : field === 'LastName'
                  ? esqTextValues.lastName
                  : field === 'DOB'
                    ? esqTextValues.dob
                    : field === 'DOD'
                      ? (esqTextValues.hideDeathDate ? '' : esqTextValues.dod)
                      : field === 'Verse2'
                        ? (esqTextValues.verse2 || '')
                        : field === 'Verse3'
                          ? (esqTextValues.verse3 || '')
                          : esqTextValues.verse;
            nextElements.push({
              id: `esq-txt-${field}-${Date.now()}`,
              type: 'text' as const,
              side: 'front' as const,
              x: Number(p.x),
              y: Number(p.y),
              width: Number(p.width),
              height: Number(p.height),
              rotation: 0,
              properties: {
                content: slotContent,
                fontSize: (() => {
                  const ppi = Number(pixelsPerInch) > 0 ? Number(pixelsPerInch) : 96;
                  const rawPx = Number(p.font_size) || 30;
                  const minIn = p.min_scale !== null && p.min_scale !== undefined ? Number(p.min_scale) : NaN;
                  const maxIn = p.max_scale !== null && p.max_scale !== undefined ? Number(p.max_scale) : NaN;
                  const minPx = Number.isFinite(minIn) && minIn > 0 ? minIn * ppi : 1;
                  const maxPx = Number.isFinite(maxIn) && maxIn > 0 ? maxIn * ppi : Number.POSITIVE_INFINITY;
                  return clampNumber(rawPx, minPx, maxPx);
                })(),
                fontFamily: (field === 'Verse' || field === 'Verse2' || field === 'Verse3' || String(field).toLowerCase().startsWith('verse'))
                  ? (() => {
                    const selectedColorObj = (colors || []).find((c: any) => Number(c.id) === Number(colorId || selectedColorId));
                    const isSelectedGrey = Boolean(
                      selectedColorObj?.code === 'MG' ||
                      selectedColorObj?.code === 'Grey' ||
                      String(selectedColorObj?.name).toUpperCase().includes('GRAY') ||
                      String(selectedColorObj?.name).toUpperCase().includes('GREY')
                    );
                    return isSelectedGrey ? 'Vermarco' : 'MonotypeCorsiva';
                  })()
                  : mapEsqDbFontToLoadedFamily(p.font_family, field),
                align: p.alignment || 'center',
                isFixedPosition: true,
                draggable: false,
                role: 'esq_fixed_text',
                minScale: p.min_scale !== null && p.min_scale !== undefined ? Number(p.min_scale) : undefined,
                maxScale: p.max_scale !== null && p.max_scale !== undefined ? Number(p.max_scale) : undefined,
                characterLimit: p.character_limit !== null && p.character_limit !== undefined ? Number(p.character_limit) : undefined,
                baseFontSize: (() => {
                  const ppi = Number(pixelsPerInch) > 0 ? Number(pixelsPerInch) : 96;
                  const maxIn = p.max_scale !== null && p.max_scale !== undefined ? Number(p.max_scale) : NaN;
                  if (Number.isFinite(maxIn) && maxIn > 0) return maxIn * ppi;
                  const minIn = p.min_scale !== null && p.min_scale !== undefined ? Number(p.min_scale) : NaN;
                  if (Number.isFinite(minIn) && minIn > 0) return minIn * ppi;
                  const rawFs = Number(p.font_size) || 30;
                  return rawFs < 5 ? rawFs * ppi : rawFs;
                })(),
                ...(rowDefault && !isSeparator ? { defaultContent: rowDefault } : {}),
                ...(isSeparator ? { esqTextReadOnly: true } : {}),
                fieldName: field,
                templateId: freshTemplate.id,
                modalId,
                colorId,
                esqBounds: hasGuideBounds ? {
                  h1: Number(p.h1),
                  h2: Number(p.h2),
                  v1: Number(p.v1),
                  v2: Number(p.v2)
                } : undefined
              }
            });
          }
        });

        // Add Fixed Artwork - use template image if Artwork position exists
        const artworkPos = findPosition('Artwork');
        if (artworkPos) {
          const p = artworkPos;
          const hasGuideBounds = p.h1 !== null && p.h1 !== undefined
            && p.h2 !== null && p.h2 !== undefined
            && p.v1 !== null && p.v1 !== undefined
            && p.v2 !== null && p.v2 !== undefined;
          // Only add the artwork element if we actually have an image for it
          if (freshTemplate.artwork_image_path) {
            const artworkSrc = buildApiUrl('/api/uploads/' + freshTemplate.artwork_image_path);
            const artworkEl = {
              id: `esq-art-Artwork-${Date.now()}`,
              type: 'artwork' as const,
              side: 'front' as const,
              x: Number(p.x),
              y: Number(p.y),
              width: Number(p.width),
              height: Number(p.height),
              rotation: 0,
              properties: {
                src: artworkSrc,
                role: 'esq_fixed_artwork',
                isFixedPosition: true,
                draggable: false,
                fieldName: 'Artwork',
                templateId: freshTemplate.id,
                modalId,
                colorId,
                esqBounds: hasGuideBounds ? {
                  h1: Number(p.h1),
                  h2: Number(p.h2),
                  v1: Number(p.v1),
                  v2: Number(p.v2)
                } : undefined
              }
            };
            nextElements.push(artworkEl);
          } // end if artwork_image_path
        }

        return { ...prev, elements: [...nextElements, tplImg] };
      });
    } catch (e) {
      console.error('Failed to apply ESQ template:', e);
    } finally {
      try { setShowPersonalTextOnCanvas(true); } catch { }
      setIsLoading(false);
    }
  }, [selectedEsqModal, selectedColorId, updateDesign, setLoadingMessage, setSampleGuideLines, setShowPersonalTextOnCanvas, esqTextValues, pixelsPerInch]);

  useEffect(() => {
    handleEsqTemplateSelectRef.current = handleEsqTemplateSelect;
  }, [handleEsqTemplateSelect]);

  useEffect(() => {
    if (designMode !== 'ESQ') return;
    const fixedTexts = (currentDesign?.elements || []).filter((el: any) =>
      el?.type === 'text' && el?.properties?.role === 'esq_fixed_text'
    );
    if (!fixedTexts.length) return;

    setEsqTextValues(prev => {
      const next = { ...prev };
      let changed = false;
      for (const el of fixedTexts) {
        if (el?.properties?.esqTextReadOnly) continue;
        const field = normalizeEsqFieldKey(el?.properties?.fieldName);
        const rawContent = String(el?.properties?.content || '').trim();
        const defaultContent = String(el?.properties?.defaultContent || '').trim();

        if (rawContent && rawContent !== 'FirstName' && rawContent !== 'LastName' && rawContent !== 'Select a Verse' && rawContent !== defaultContent) {
          const normalized = normalizeEsqFieldContent(
            field,
            rawContent,
            defaultContent
          );
          if (field === 'firstname' && normalized !== undefined) {
            if (isEsqSharedNameLimitTemplate) {
              if (String(normalized).trim() !== String(next.fullName || '').trim()) { next.fullName = normalized; changed = true; }
            } else {
              if (String(normalized).trim() !== String(next.firstName || '').trim()) { next.firstName = normalized; changed = true; }
            }
          }
          if (field === 'lastname' && normalized !== undefined) {
            if (String(normalized).trim() !== String(next.lastName || '').trim()) { next.lastName = normalized; changed = true; }
          }
          if (field === 'dob' && normalized && String(normalized).trim() !== String(next.dob || '').trim()) { next.dob = normalized; changed = true; }
          if (field === 'dod' && normalized && String(normalized).trim() !== String(next.dod || '').trim()) { next.dod = normalized; changed = true; }
          if (field === 'verse' && normalized && String(normalized).trim() !== String(next.verse || '').trim()) { next.verse = String(el?.properties?.content || ''); changed = true; }
          if (field === 'verse2' && normalized && String(normalized).trim() !== String(next.verse2 || '').trim()) { next.verse2 = String(el?.properties?.content || ''); changed = true; }
          if (field === 'verse3' && normalized && String(normalized).trim() !== String(next.verse3 || '').trim()) { next.verse3 = String(el?.properties?.content || ''); changed = true; }
        }
      }
      if (changed) {
        try { localStorage.setItem(ESQ_TEXT_CACHE_KEY, JSON.stringify(next)); } catch { }
        return next;
      }
      return prev;
    });
  }, [designMode, currentDesign?.elements, normalizeEsqFieldContent, isEsqSharedNameLimitTemplate]);

  const esqFieldCharacterLimits = useMemo(() => {
    const limits: Partial<Record<keyof EsqTextFormValues, number>> = {};
    for (const el of currentDesign?.elements || []) {
      if (el?.type !== 'text' || el?.properties?.role !== 'esq_fixed_text') continue;
      const lim = Number(el?.properties?.characterLimit || 0);
      if (!(lim > 0)) continue;
      const key = normalizeEsqFieldKey(el?.properties?.fieldName);
      if (key === 'firstname') limits.firstName = lim;
      if (key === 'lastname') limits.lastName = lim;
      if (key === 'verse') limits.verse = lim;
    }
    if (isEsqSharedNameLimitTemplate) {
      (limits as any).fullName = limits.firstName ?? limits.lastName;
    }
    return limits;
  }, [currentDesign?.elements, isEsqSharedNameLimitTemplate]);

  const sharedNameLimit = useMemo(() => {
    if (!isEsqSharedNameLimitTemplate) return undefined;
    return (esqFieldCharacterLimits as any).firstName || (esqFieldCharacterLimits as any).lastName;
  }, [isEsqSharedNameLimitTemplate, esqFieldCharacterLimits]);

  const fetchColorMappedArtworkBySource = useCallback(async (
    elements: any[],
    fromColorId: number | null | undefined,
    toColorId: number | null | undefined,
    modelId: number,
    sizeId: number
  ): Promise<Record<number, any>> => {
    if (!modelId || !sizeId || !fromColorId || !toColorId || Number(fromColorId) === Number(toColorId)) return {};

    const normalizeGranite = (value: string) => (String(value || '').toUpperCase().includes('PJB') ? 'PJB' : 'MG');
    const fromColorName = sharedColors.find((c: any) => Number(c?.id) === Number(fromColorId))?.name;
    const toColorName = sharedColors.find((c: any) => Number(c?.id) === Number(toColorId))?.name;
    const fromGranite = normalizeGranite(getGraniteForColor(Number(fromColorId), fromColorName));
    const toGranite = normalizeGranite(getGraniteForColor(Number(toColorId), toColorName));
    if (fromGranite === toGranite) return {};

    const artworkIds = Array.from(new Set(
      (elements || [])
        .filter((el: any) => {
          if (el?.type !== 'artwork') return false;
          const role = String(el?.properties?.role || '').toLowerCase();
          if (role === 'template' || role === 'vase' || role === 'porcelain' || role === 'esq_fixed_artwork') return false;
          if (el?.properties?.isCustomUpload) return false;
          return Number(el?.properties?.artworkId || 0) > 0;
        })
        .map((el: any) => Number(el?.properties?.artworkId || 0))
        .filter((id: number) => id > 0)
    ));

    if (!artworkIds.length) return {};

    const res = await fetch(buildApiUrl('/api/art/mapping/resolve'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model_id: modelId,
        size_id: sizeId,
        from_granite: fromGranite,
        to_granite: toGranite,
        artwork_ids: artworkIds,
      }),
    });
    const json = await res.json().catch(() => ({}));
    const items = Array.isArray(json?.items) ? json.items : [];
    const bySource: Record<number, any> = {};
    for (const item of items) {
      const sourceId = Number(item?.source_artwork_id || 0);
      if (sourceId > 0) bySource[sourceId] = item;
    }
    return bySource;
  }, [sharedColors]);

  const handleColorSelect = useCallback(async (
    colorId: number,
    colorIndex: number,
    isInitial = false,
    forceApply = false
  ): Promise<boolean> => {
    const reqId = ++colorPreviewReqIdRef.current;
    const esqReqId = ++esqSwitchReqIdRef.current;
    const isStaleColorReq = () => reqId !== colorPreviewReqIdRef.current;
    const isStaleEsqReq = () => esqReqId !== esqSwitchReqIdRef.current;
    if (isApplyingDesign || (window as any).__isApplyingDesign) {
      if (!isInitial && !forceApply) return true;
    }
    if (!isInitial && !forceApply && Number(selectedColorId) === Number(colorId)) {
      return true;
    }
    const selectedColorMeta = sharedColors.find((c: any) => Number(c?.id) === Number(colorId))
      || sharedColors[colorIndex]
      || null;
    const selectedColorNameNext = selectedColorMeta?.name ? String(selectedColorMeta.name) : undefined;
    const prevTemplateEl = currentDesign.elements.find(isTemplateElement) as any | undefined;
    const hadAppliedEsqTemplate = (currentDesign?.elements || []).some((el: any) => {
      const role = String(el?.properties?.role || '').toLowerCase();
      return role === 'template_design' || role === 'esq_fixed_text' || role === 'esq_fixed_artwork';
    });
    try { setIsApplyingDesign && setIsApplyingDesign(true); } catch { }
    setLoadingMessage('Loading granite...');
    try { if (designMode !== 'ESQ') setShowPersonalTextOnCanvas(false); } catch { }

    // If the user previously selected an artwork placement (e.g. Left/Top),
    // do not carry that navigation state across color switches. Otherwise the
    // Art modal will auto-open on the old placement after switching colors.
    try { setActivePlacementSlot && setActivePlacementSlot(null); } catch { }

    const skipVaseFilter = isApplyingDesign || (window as any).__isApplyingDesign;

    if (designMode !== 'ESQ') {
      // Clear template and vases immediately to prevent overlap while loading
      if (setSelectedGraniteImage) setSelectedGraniteImage(null);
      if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(null);

      updateDesign(prev => ({
        ...prev,
        // Preserve user-entered text while replacing only color-dependent visual layers.
        elements: prev.elements.filter(el => !isTemplateElement(el) && (skipVaseFilter || !isVaseElement(el)))
      }));
    }

    try {
      if (designMode === 'ESQ') {
        // Keep current ESQ text/template selection while switching color; this avoids
        // transient blank states when modal/template counterpart fetch is delayed.
        if (setSelectedGraniteImage) setSelectedGraniteImage(null);
        if (setSelectedGraniteBaseImage) setSelectedGraniteBaseImage(null);
        updateDesign(prev => ({
          ...prev,
          selectedColorId: colorId,
          selectedColorIndex: colorIndex,
          ...(selectedColorNameNext ? { selectedColorName: selectedColorNameNext } : {}),
          // Keep text fields/content intact when changing ESQ colors.
          elements: (prev.elements || []).filter((el: any) =>
            !isTemplateElement(el) &&
            !isEsqTemplateLayerElement(el) &&
            (skipVaseFilter || !isVaseElement(el))
          )
        }));

        // SYNC: If we have an ESQ modal selected, swap to its counterpart in the new color
        if (selectedEsqModal) {
          try {
            const url = buildApiUrl(`/api/esq/modals?color_id=${colorId}`);
            const res = await fetch(url);
            const data = await res.json();
            if (isStaleColorReq() || isStaleEsqReq()) return false;
            const modals = Array.isArray(data) ? data : [];

            const matchedModal = modals.find((m: any) =>
              m.name === selectedEsqModal.name && m.category === selectedEsqModal.category
            );

            if (matchedModal) {
              await handleEsqModalSelect(matchedModal, esqReqId, colorId);
              if (isStaleColorReq() || isStaleEsqReq()) return false;

              // Keep template/text continuity across ESQ color changes by re-applying
              // the equivalent template for the newly selected color.
              if (hadAppliedEsqTemplate && selectedEsqTemplate) {
                try {
                  const tplRes = await fetch(buildApiUrl(`/api/esq/templates/${matchedModal.id}/${colorId}`));
                  const tplData = await tplRes.json();
                  if (isStaleColorReq() || isStaleEsqReq()) return false;

                  const templates = Array.isArray(tplData) ? tplData : [];
                  const prevTplId = Number((selectedEsqTemplate as any)?.id || 0);
                  const prevTplName = String(
                    (selectedEsqTemplate as any)?.name ||
                    (selectedEsqTemplate as any)?.template_name ||
                    ''
                  ).trim().toLowerCase();

                  const matchedTemplate =
                    (prevTplName ? templates.find((t: any) => String(t?.name || t?.template_name || '').trim().toLowerCase() === prevTplName) : null) ||
                    templates.find((t: any) => Number(t?.id || 0) === prevTplId) ||
                    null;

                  if (matchedTemplate) {
                    await handleEsqTemplateSelect({
                      ...matchedTemplate,
                      modalId: matchedModal.id,
                      colorId
                    });
                    if (isStaleColorReq() || isStaleEsqReq()) return false;
                  }
                } catch {
                  // ESQ template sync soft failure — non-critical
                }
              }
            } else {
              // Fallback: if counterpart is missing, keep visuals unchanged and only
              // sync color metadata so the UI doesn't enter a half-cleared state.
              if (isStaleColorReq() || isStaleEsqReq()) return false;
              updateDesign(prev => ({
                ...prev,
                selectedColorId: colorId,
                selectedColorIndex: colorIndex,
                ...(selectedColorNameNext ? { selectedColorName: selectedColorNameNext } : {}),
              }));
            }
          } catch (e) {
            console.error('ESQ sync failed:', e);
          }
        } else {
          // No selected ESQ modal yet; just sync selected color in state.
          updateDesign(prev => ({
            ...prev,
            selectedColorId: colorId,
            selectedColorIndex: colorIndex,
            ...(selectedColorNameNext ? { selectedColorName: selectedColorNameNext } : {}),
          }));
        }

        // ESQ: re-enable fixed text rendering after color switch completes
        if (isStaleColorReq() || isStaleEsqReq()) {
          try { setShowPersonalTextOnCanvas(true); } catch { }
          return false;
        }
        try { setShowPersonalTextOnCanvas(true); } catch { }
        try { setActivePlacementSlot && setActivePlacementSlot(null); } catch { }
        setIsApplyingDesign?.(false);
        setIsLoading(false);
        setLoadingMessage('');
        return true;
      }

      const modelIdToUse = Number(selectedGraniteMeta?.modelId || currentModelId || 0);
      const sizeIdToUse = Number(selectedGraniteMeta?.sizeId || (currentDesign as any)?.selectedSizeId || 0);
      let resolvedSrc = '';
      if (modelIdToUse && sizeIdToUse) {
        // Use shared fetchDesignPreview for caching and deduplication
        const data = await fetchDesignPreview(modelIdToUse, sizeIdToUse, colorId);
        if (data?.status === 'success') {
          resolvedSrc = data?.data?.preview_api_url || data?.data?.model_img_api_url || '';
        }
      }
      if (!resolvedSrc) { setIsApplyingDesign?.(false); return false; }
      // Use the same design dimensions as Designer.tsx (DESIGN_WIDTH=1400, DESIGN_HEIGHT=700)
      const stageWidth = 1400;
      const stageHeight = 700;
      let widthToUse = prevTemplateEl?.width || 0;
      let heightToUse = prevTemplateEl?.height || 0;
      if (!widthToUse || !heightToUse) {
        const loadedImg = await loadImageWithRetry(resolvedSrc);
        widthToUse = loadedImg.naturalWidth || 600;
        heightToUse = loadedImg.naturalHeight || 400;
      }
      const mappedBySourceId = await fetchColorMappedArtworkBySource(
        currentDesign.elements || [],
        selectedColorId,
        colorId,
        modelIdToUse,
        sizeIdToUse
      );
      updateDesign(prev => {
        const nextElements = prev.elements
          .filter(el => !isTemplateElement(el))
          .map((el: any) => {
            if (el?.type !== 'artwork') return el;
            const role = String(el?.properties?.role || '').toLowerCase();
            if (role === 'template' || role === 'vase' || role === 'porcelain' || role === 'esq_fixed_artwork') return el;
            if (el?.properties?.isCustomUpload) return el;
            const sourceId = Number(el?.properties?.artworkId || 0);
            const mapped = sourceId > 0 ? mappedBySourceId[sourceId] : null;
            if (!mapped) return el;
            return {
              ...el,
              properties: {
                ...el.properties,
                src: mapped.target_image_url || mapped.target_thumb_url || el.properties?.src,
                alt: mapped.target_title || mapped.target_art_code || el.properties?.alt,
                code: mapped.target_art_code || el.properties?.code,
                artCode: mapped.target_art_code || el.properties?.artCode,
                artworkId: Number(mapped.target_artwork_id || el.properties?.artworkId || 0),
                type: mapped.target_type || el.properties?.type,
              },
            };
          });
        const newTemplate = {
          id: 'main-template', type: 'artwork' as const,
          x: prevTemplateEl?.x ?? Math.floor((stageWidth - widthToUse) / 2),
          y: prevTemplateEl?.y ?? Math.floor((stageHeight - heightToUse) / 2),
          width: widthToUse, height: heightToUse, rotation: prevTemplateEl?.rotation ?? 0,
          properties: {
            role: 'template', isTemplate: true, src: resolvedSrc, colorId, modelId: modelIdToUse, sizeId: sizeIdToUse,
            isFlatMarker: prevTemplateEl?.properties?.isFlatMarker ?? true
          }
        };
        return {
          ...prev,
          selectedColorId: colorId,
          selectedColorIndex: colorIndex,
          ...(selectedColorNameNext ? { selectedColorName: selectedColorNameNext } : {}),
          elements: [...nextElements, newTemplate]
        };
      });
      setSelectedGraniteImage?.(resolvedSrc); setIsTemplateSelected(true);
      if (setSelectedGraniteMeta && selectedGraniteMeta) {
        setSelectedGraniteMeta({ ...selectedGraniteMeta, colorId, src: resolvedSrc });
      } else if (setSelectedGraniteMeta && modelIdToUse && sizeIdToUse) {
        setSelectedGraniteMeta({ modelId: modelIdToUse, sizeId: sizeIdToUse, colorId, src: resolvedSrc });
      }
      if (modelIdToUse && sizeIdToUse) {
        await applyDefaultVase(colorId, modelIdToUse, sizeIdToUse);
      }
      try { setShowPersonalTextOnCanvas(true); } catch { }
    } catch (error) { } finally {
      try { setShowPersonalTextOnCanvas(true); } catch { }
      if (!isStaleColorReq()) {
        setIsLoading(false);
        try { setIsApplyingDesign && setIsApplyingDesign(false); } catch { }
      }
    }
    return true;
  }, [
    selectedColorId,
    currentDesign,
    selectedGraniteMeta,
    updateDesign,
    currentModelId,
    setSelectedGraniteImage,
    setSelectedGraniteMeta,
    setIsTemplateSelected,
    setIsLoading,
    setLoadingMessage,
    setIsApplyingDesign,
    fetchDesignPreview,
    applyDefaultVase,
    designMode,
    selectedEsqModal,
    selectedEsqTemplate,
    handleEsqModalSelect,
    handleEsqTemplateSelect,
    setActivePlacementSlot,
    fetchColorMappedArtworkBySource
    , sharedColors
  ]);

  const handleShapeSelect = useCallback(async (modelId: number, sizeLabel?: string, explicitSizeId?: number) => {
    const colorId = selectedColorId ?? (currentDesign as any)?.selectedColorId ?? selectedGraniteMeta?.colorId;
    const sizeId = explicitSizeId ?? selectedGraniteMeta?.sizeId ?? (currentDesign as any)?.selectedSizeId;

    try { setIsApplyingDesign && setIsApplyingDesign(true); } catch { }
    if (colorId && modelId && sizeId) {
      await applyDefaultVase(Number(colorId), Number(modelId), Number(sizeId), sizeLabel);
    }
    try { setActivePlacementSlot && setActivePlacementSlot(null); } catch { }
    try { setIsApplyingDesign && setIsApplyingDesign(false); } catch { }
  }, [selectedColorId, currentDesign, selectedGraniteMeta, applyDefaultVase, setIsApplyingDesign, setActivePlacementSlot]);

  // 2. Effects
  // Re-initialize/sync ESQ modal state from template ESQ ID (e.g. after refresh or stale localStorage)
  useEffect(() => {
    if (designMode === 'ESQ') {
      if (currentDesign?.activeSide === 'back') {
        try { setActiveSide?.('front'); } catch { }
      }
      // Find the template element that has an ESQ ID assigned
      const templateEl = currentDesign?.elements?.find(el => {
        const role = String(el.properties?.role || '').toLowerCase();
        const isTpl = (role === 'template' || el.properties?.isTemplate || role === 'template_design');
        return isTpl && (el.properties?.esqModalId || el.properties?.modalId);
      });

      const modalId = Number(
        templateEl?.properties?.esqModalId ||
        templateEl?.properties?.modalId ||
        (currentDesign as any)?.selectedEsqModalId ||
        (currentDesign as any)?.selectedModelId ||
        0
      );

      const currentModalId = Number(selectedEsqModal?.id || 0);

      // Sync when missing OR mismatched to avoid wrong color counterpart selection.
      if (modalId > 0 && currentModalId !== modalId) {
        fetch(buildApiUrl(`/api/esq/modal/${modalId}`))
          .then(res => {
            if (!res.ok) throw new Error('Modal not found');
            return res.json();
          })
          .then(data => {
            if (data && data.id) {
              setSelectedEsqModal(data);
            }
          })
          .catch(err => {
            if (err.message !== 'Modal not found') {
              console.error('[LeftPanel] Failed to restore ESQ modal state:', err);
            }
          });
      }

      // Also sync selectedEsqTemplate if missing or mismatched
      const templateId = Number(
        templateEl?.properties?.templateId ||
        templateEl?.properties?.esqTemplateId ||
        (currentDesign as any)?.selectedEsqTemplateId ||
        0
      );
      const currentTemplateId = Number(selectedEsqTemplate?.id || 0);
      if (modalId > 0 && templateId > 0 && currentTemplateId !== templateId) {
        const colorId = Number((currentDesign as any)?.selectedColorId || templateEl?.properties?.colorId || 5);
        fetch(buildApiUrl(`/api/esq/templates/${modalId}/${colorId}`))
          .then(res => res.json())
          .then(data => {
            const list = Array.isArray(data) ? data : [];
            const found = list.find((t: any) => Number(t.id) === templateId);
            if (found) {
              setSelectedEsqTemplate(found);
            }
          })
          .catch(() => { });
      }
    }
  }, [designMode, selectedEsqModal, selectedEsqTemplate, currentDesign?.elements, (currentDesign as any)?.selectedModelId, (currentDesign as any)?.selectedEsqModalId, setSelectedEsqModal, setSelectedEsqTemplate]);

  // Restoration and sync already handled by individual modal selection handlers

  const loadTextData = useCallback(async () => {
    // Only load once unless specifically forced or if we're in a critical loading state (isApplyingDesign)
    if (areVersesLoaded && !isApplyingDesign) return;

    try {
      // Parallelize verse category and verse fetches
      const [catRes, versesRes] = await Promise.all([
        fetch(buildApiUrl('/api/verses/categories')),
        fetch(buildApiUrl('/api/verses')),
      ]);

      const [catData, versesData] = await Promise.all([
        catRes.json(),
        versesRes.json(),
      ]);

      if (catData.status === 'success') {
        const fetched = Array.isArray(catData.data) ? catData.data : [];
        const unique = Array.from(new Set(['All', ...fetched]));
        setCachedCategories([...unique, 'Custom']);
      }

      if (versesData.status === 'success') {
        setCachedVerses(versesData.data.verses);
      }

      setAreVersesLoaded(true);
    } catch (e) {
      console.error("Delayed data fetch failed", e);
    }
  }, [areVersesLoaded, isApplyingDesign]);


  // When a saved design is opened, mirror its selectedColor into the LeftPanel's
  // local UI state without invoking template regeneration. This keeps the color
  // chip selection consistent with the loaded design (e.g., black vs grey).
  // Color synchronization is now handled directly by useDesignContext and handleColorSelect


  useEffect(() => {
    // Only trigger heavy background load if we actually have vases/text in the design
    // that need color-matching or if the relevant modals are opened.
    const hasVasesOrText = designElements.some(el => isVaseElement(el) || isTextElement(el));

    if (openModal === 'text' || (hasVasesOrText && isApplyingDesign)) {
      loadTextData();
    }
  }, [openModal, loadTextData, designElements, isApplyingDesign]);






  // Sync colors from shared context
  useEffect(() => {
    if (sharedColors.length > 0) {
      setColors(sharedColors);
    }
  }, [sharedColors]);

  // Check vase availability when model, size, or color changes.
  // Uses vase_image from the designs table as the source of truth.
  useEffect(() => {
    let cancelled = false;

    const updateVaseCompatibility = async () => {
      if (designMode === 'ESQ') {
        // Flat Markers don't use vases
        const isFlatMarker = selectedEsqModal?.category === 'Flat Markers';
        if (!cancelled) setVasesDisabled(isFlatMarker);
        return;
      }

      const modelId = Number(selectedGraniteMeta?.modelId || currentModelId || 0);
      const sizeId = Number(selectedGraniteMeta?.sizeId || (currentDesign as any)?.selectedSizeId || 0);
      const colorId = selectedColorId ?? (currentDesign as any)?.selectedColorId ?? null;

      if (!modelId || !sizeId || !colorId) {
        if (!cancelled) setVasesDisabled(true);
        return;
      }

      const isAllowed = await checkVasesAllowed(modelId, sizeId, colorId);
      if (!cancelled) setVasesDisabled(!isAllowed);
    };

    updateVaseCompatibility();

    return () => {
      cancelled = true;
    };
  }, [selectedGraniteMeta?.modelId, currentModelId, selectedGraniteMeta?.sizeId, (currentDesign as any)?.selectedModelId, (currentDesign as any)?.selectedSizeId, selectedColorId, (currentDesign as any)?.selectedColorId, checkVasesAllowed, designMode, selectedEsqModal?.category]);

  // Event handlers
  const handleArtworkSelect = useCallback((artwork: any) => {
    setOpenModal(null);
  }, []);


  const handleTextConfirm = useCallback((formData: any) => {
    setOpenModal(null);
  }, []);


  const handlePhotoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setIsUploadingPhoto(true);
    const reader = new FileReader();

    reader.onload = async () => {
      const dataUrl = reader.result as string;
      try {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Image load failed'));
          img.src = dataUrl;
        });

        // Do NOT crop the source image. Keep full aspect ratio for adjustments.
        // Just resize if it's too large to maintain performance.
        const maxRes = 1024;
        let sw = img.naturalWidth || img.width;
        let sh = img.naturalHeight || img.height;

        let outW = sw;
        let outH = sh;

        if (sw > maxRes || sh > maxRes) {
          const ratio = sw / sh;
          if (sw > sh) {
            outW = maxRes;
            outH = Math.floor(maxRes / ratio);
          } else {
            outH = maxRes;
            outW = Math.floor(maxRes * ratio);
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        // Draw full image, scaled down if needed, but not cropped
        ctx.drawImage(img, 0, 0, outW, outH);
        const fullDataUrl = canvas.toDataURL('image/jpeg', 0.85);

        // Define target dimensions for the ELEMENT (3:4 Oval)
        const targetRatio = 0.75;
        const ppiRaw = Number((window as any)?.__appPixelsPerInch || 0);
        const ppi = ppiRaw > 0.0001 ? ppiRaw : 96;
        const cmToIn = (cm: number) => cm / 2.54;

        // Porcelain default: 9x12 cm
        const desiredW = cmToIn(9) * ppi;
        const desiredH = cmToIn(12) * ppi; // derived from 9x12cm, approx 3:4

        const guidesW = layoutGuides ? Math.max(1, (layoutGuides.right - layoutGuides.left)) : 0;
        const guidesH = layoutGuides ? Math.max(1, (layoutGuides.bottom - layoutGuides.top)) : 0;

        // Try to keep it at 100% real-world size (9x12cm) initially.
        // Only scale down if it exceeds the active design area (with some padding).
        const availW = guidesW > 0 ? guidesW : (window.innerWidth < 768 ? window.innerWidth * 0.8 : 1000); // Fallback usually wide enough
        const availH = guidesH > 0 ? guidesH : (window.innerHeight < 600 ? window.innerHeight * 0.6 : 600); // Fallback usually tall enough

        // Calculate scale: Default to 1 (True Size), but shrink if it literally doesn't fit in the workspace.
        const scale = Math.min(
          1,
          (availW * 0.9) / desiredW,
          (availH * 0.9) / desiredH
        );

        // Element Dimensions: Strictly 3:4 oval, based on the physical size target
        const w = Math.max(64, Math.floor(desiredW * scale));
        const h = Math.floor(w / targetRatio);

        updateDesign((prev: any) => {
          // Center on the granite stone (preview_png).
          // Use the template element for stone bounds; if absent, fall back to canvas size.
          const tpl = (prev.elements || []).find((e: any) =>
            e.properties?.role === 'template' || e.properties?.isTemplate === true
          );
          const stoneX = tpl ? (renderedTemplateOrigin?.x ?? tpl.x ?? 0) : 0;
          const stoneY = tpl ? (renderedTemplateOrigin?.y ?? tpl.y ?? 0) : 0;
          const stoneW = tpl?.width || (prev?.canvas_width || 1200);
          const stoneH = tpl?.height || (prev?.canvas_height || 600);
          const stageCenterX = stoneX + stoneW / 2;
          const stageCenterY = stoneY + stoneH / 2;


          // Check if there is an empty porcelain placeholder frame to replace
          const porcelainIndex = (prev.elements || []).findIndex((e: any) =>
            e.properties?.isPorcelainPlaceholder === true
            && e.side === (prev.activeSide || 'front')
          );



          let nextElements = [...(prev.elements || [])];
          let finalX = Math.floor(stageCenterX - w / 2);
          let finalY = Math.floor(stageCenterY - h / 2);
          let finalW = w;
          let finalH = h;
          let finalSlotType = null;
          let isFixed = false;

          if (porcelainIndex !== -1) {
            const p = nextElements[porcelainIndex];

            finalX = p.x;
            finalY = p.y;
            finalW = p.width;
            finalH = p.height;
            finalSlotType = 'porcelain';
            isFixed = true;
            // Remove the placeholder
            nextElements.splice(porcelainIndex, 1);
          } else {
            // Check if there's a special artwork - if so, we should look for porcelain slot dimensions
            const specialArtwork = (prev.elements || []).find((e: any) =>
              e.type === 'artwork' &&
              e.properties?.slotType === 'special' &&
              e.side === (prev.activeSide || 'front')
            );

            if (specialArtwork && specialArtwork.properties?.placementCode) {
              // Find the porcelain slot for this special artwork
              const porcelainSlot = placementSlots?.find((s: any) =>
                s.slot_type === 'porcelain' &&
                s.placement_code === specialArtwork.properties.placementCode
              );

              const template = (prev.elements || []).find((e: any) => e.properties?.role === 'template' || e.properties?.isTemplate);

              if (porcelainSlot && template) {

                const ox = renderedTemplateOrigin?.x ?? (template.x || 0);
                const oy = renderedTemplateOrigin?.y ?? (template.y || 0);
                const ppi = pixelsPerInch > 0 ? pixelsPerInch : 96;

                finalX = ox + Number(porcelainSlot.v1) * ppi;
                finalY = oy + Number(porcelainSlot.h1) * ppi;
                finalW = (Number(porcelainSlot.v2) - Number(porcelainSlot.v1)) * ppi;
                finalH = (Number(porcelainSlot.h2) - Number(porcelainSlot.h1)) * ppi;
                finalSlotType = 'porcelain';
                isFixed = true;
              }
            }

            if (!finalSlotType) {

            }
          }

          const el = {
            id: `user-art-${Date.now()}`,
            type: 'artwork',
            side: prev.activeSide || 'front',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            rotation: 0,
            properties: {
              role: finalSlotType === 'porcelain' ? 'porcelain' : 'work',
              src: fullDataUrl,           // Initial display is full image
              originalSrc: fullDataUrl,   // SAVE FULL IMAGE SOURCE for re-editing
              userUploaded: true,
              shape: 'oval',
              userResized: true,
              slotType: finalSlotType,
              isFixedPosition: isFixed,
              draggable: !isFixed,
              adjustment: {               // Initialize default adjustments
                zoom: 1,
                rotation: 0,
                isGreyscale: false,
                xOffset: 0,
                yOffset: 0,
                maskType: 'oval'
              }
            }
          } as any;

          return { ...prev, elements: [...nextElements, el] };
        });

      } catch (err) {
      } finally {
        setIsUploadingPhoto(false);
        // Reset file input so same file can be uploaded again if needed
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  }, [updateDesign, layoutGuides]);

  // Handle general image upload (adds artwork to canvas)
  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      try {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Image load failed'));
          img.src = dataUrl;
        });

        // Calculate dimensions - limit max size for performance
        const maxRes = 1024;
        let sw = img.naturalWidth || img.width;
        let sh = img.naturalHeight || img.height;

        let outW = sw;
        let outH = sh;

        if (sw > maxRes || sh > maxRes) {
          const ratio = sw / sh;
          if (sw > sh) {
            outW = maxRes;
            outH = Math.floor(maxRes / ratio);
          } else {
            outH = maxRes;
            outW = Math.floor(maxRes * ratio);
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        ctx.drawImage(img, 0, 0, outW, outH);
        const fullDataUrl = canvas.toDataURL('image/jpeg', 0.85);

        // Add element to design centered on canvas
        updateDesign((prev: any) => {
          const tpl = (prev.elements || []).find((e: any) =>
            e.properties?.role === 'template' || e.properties?.isTemplate === true
          );
          const stoneX = tpl ? (renderedTemplateOrigin?.x ?? tpl.x ?? 0) : 0;
          const stoneY = tpl ? (renderedTemplateOrigin?.y ?? tpl.y ?? 0) : 0;
          const stoneW = tpl?.width || (prev?.canvas_width || 1200);
          const stoneH = tpl?.height || (prev?.canvas_height || 600);

          // Default size: 30% of stone width or natural size, whichever is smaller
          const defaultW = Math.min(outW, Math.floor(stoneW * 0.3));
          const ratio = outH / outW;
          const defaultH = Math.floor(defaultW * ratio);

          const finalX = Math.floor(stoneX + (stoneW - defaultW) / 2);
          const finalY = Math.floor(stoneY + (stoneH - defaultH) / 2);

          const el = {
            id: `uploaded-img-${Date.now()}`,
            type: 'artwork',
            side: prev.activeSide || 'front',
            x: finalX,
            y: finalY,
            width: defaultW,
            height: defaultH,
            rotation: 0,
            properties: {
              role: 'work',
              src: fullDataUrl,
              originalSrc: fullDataUrl,
              userUploaded: true,
              isCustomUpload: true,
              draggable: true,
            }
          } as any;

          return { ...prev, elements: [...(prev.elements || []), el] };
        });
      } catch (err) {
        console.error('Image upload failed:', err);
      } finally {
        // Reset file input so same file can be uploaded again
        if (imageUploadRef.current) imageUploadRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  }, [updateDesign, renderedTemplateOrigin]);



  const getModalTitle = useCallback(() => {
    switch (openModal) {
      case 'art': return isTemplateSelected ? 'Select Artwork' : 'Artwork Library';
      case 'shapes': return 'Select Model';
      case 'esq-modals': return 'Select Model';
      case 'esq-templates': return 'Select Template';
      case 'vases': return 'Select Vase';
      case 'text': return (currentDesign?.activeSide === 'back' ? 'Back Side Text' : 'Personal Details');
      case 'porcelain': return 'Adjust Porcelain Photo';
      case 'artwork-mapping': return 'Artwork Mapping';
      case 'mode': return 'Category';
      case 'esq-text': return 'ESQ Text';
      default: return '';
    }
  }, [openModal, isTemplateSelected, currentDesign?.activeSide]);

  useEffect(() => {
    setIsGlobalLoading(isLoading);
  }, [isLoading, setIsGlobalLoading]);

  // Effects
  useEffect(() => {
    if (isLoading) {
      setOpenModal(null);
    }

    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Body scroll lock
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [openModal]);

  return (
    <Panel>

      <ButtonBar>
        <Colors
          selectedColorId={selectedColorId ?? null}
          selectedColorIndex={selectedColorIndex ?? 0}
          onColorSelect={handleColorSelect}
          setForcedGranite={setForcedGranite}
        />

        <IconButton
          title="Category"
          onClick={() => setOpenModal('mode')}
        >
          <RefreshCw size={22} />
          <span>Category</span>
        </IconButton>

        {effectiveDesignMode === 'LSP/QD' ? (
          <>
            <IconButton
              title="Models"
              onClick={() => {
                try { setCurrentTool && setCurrentTool('model'); } catch { }
                loadShapesData();
                setHasOpenedShapes(true);
                setOpenModal('shapes');
              }}
            >
              <Square size={22} />
              <span>Models</span>
            </IconButton>

            <IconButton
              title={isTemplateSelected ? "Art/Panels" : "Artwork Library"}
              onClick={() => { try { setCurrentTool && setCurrentTool('art'); } catch { } setOpenModal('art'); }}
              disabled={false}
            >
              <ImageIcon size={22} />
              <span>Art</span>
            </IconButton>

            {isEditorUser && (
              <IconButton
                title="Artwork Mapping"
                onClick={() => setOpenModal('artwork-mapping')}
              >
                <Palette size={22} />
                <span>Map Art</span>
              </IconButton>
            )}

            <IconButton
              title={isTemplateSelected ? "Text" : "Select a template to enable text customization."}
              onClick={() => { try { setCurrentTool && setCurrentTool('text'); } catch { } loadTextData(); loadTextAssets(); try { setShowPersonalTextOnCanvas(true); } catch { } setOpenModal('text'); }}
              disabled={!isTemplateSelected}
            >
              <TypeIcon size={22} />
              <span>Text</span>
            </IconButton>

            <IconButton
              title={porcelainTitle}
              onClick={() => { if (!porcelainDisabled) { try { setCurrentTool && setCurrentTool('porcelain'); } catch { } setOpenModal('porcelain'); } }}
              disabled={porcelainDisabled}
            >
              <Camera size={22} />
              <span>Porcelain</span>
            </IconButton>

            <IconButton
              title={vasesDisabled ? "Vases disabled for this shape" : "Vases"}
              onClick={() => { if (!vasesDisabled) { try { setCurrentTool && setCurrentTool('vase'); } catch { } setOpenModal('vases'); } }}
              disabled={vasesDisabled}
            >
              <PackageIcon size={22} />
              <span>Vases</span>
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              title="Models"
              onClick={() => setOpenModal('esq-modals')}
            >
              <Square size={22} />
              <span>Models</span>
            </IconButton>

            <IconButton
              title="Templates"
              onClick={() => setOpenModal('esq-templates')}
              disabled={!selectedEsqModal}
            >
              <ImageIcon size={22} />
              <span>Templates</span>
            </IconButton>

            <IconButton
              title="Text"
              onClick={() => setOpenModal('esq-text')}
            >
              <TypeIcon size={22} />
              <span>Text</span>
            </IconButton>

            {isEditorUser && (
              <IconButton
                title="Upload Image"
                onClick={() => imageUploadRef.current?.click()}
              >
                <Upload size={22} />
                <span>Upload</span>
              </IconButton>
            )}

            <IconButton
              title="Vases"
              onClick={() => { if (!vasesDisabled) setOpenModal('vases'); }}
              disabled={vasesDisabled}
            >
              <PackageIcon size={22} />
              <span>Vases</span>
            </IconButton>
          </>
        )}


      </ButtonBar>

      <PanelBody>
        <SupportSection>
          <SupportTitle>Need quick help?</SupportTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <SupportButton onClick={() => setOpenModal('faq')}>
              View FAQ
            </SupportButton>
            <SupportButton
              style={{ background: '#1e3a54', boxShadow: '0 4px 12px rgba(30, 58, 84, 0.25)' }}
              onClick={() => {
                const guideUrl = effectiveDesignMode === 'ESQ' ? '/esq-user-guide' : '/lsp-qd-user-guide';
                window.open(guideUrl, '_blank');
              }}
            >
              User Guide
            </SupportButton>
          </div>
        </SupportSection>
      </PanelBody>

      {/* Note: LeftPanel intentionally does not render a full-screen overlay Preloader.
          Individual child panels (Art, Shapes, Vases, Text) may still show their own
          local preloaders where appropriate. */}

      {/* Modal System */}
      {openModal && openModal !== 'shapes' && openModal !== 'text' && (
        <ModalOverlay>
          <ModalCard
            $isArtModal={openModal === 'art'}
            $isArtGridActive={openModal === 'art' && isArtImageGridActive}
            $isPorcelainModal={openModal === 'porcelain'}
            $isEsqModal={openModal === 'esq-modals' || openModal === 'esq-templates' || openModal === 'esq-text'}
            $isCategoryModal={openModal === 'mode'}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <span>{getModalTitle()}</span>
              <CloseBtn onClick={() => setOpenModal(null)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody $noPadding={openModal === 'porcelain'}>
              {openModal === 'mode' && (
                <ModeGrid>
                  <ModeOption
                    type="button"
                    $active={effectiveDesignMode === 'LSP/QD'}
                    onClick={() => {
                      try {
                        sessionStorage.setItem('design_tool.mode_selected', 'true');
                      } catch { }
                      setDesignMode('LSP/QD');
                      setOpenModal(null);
                    }}
                  >
                    <ModeTitle>LSP / QD</ModeTitle>
                  </ModeOption>
                  <ModeOption
                    type="button"
                    $active={effectiveDesignMode === 'ESQ'}
                    onClick={() => {
                      try {
                        sessionStorage.setItem('design_tool.mode_selected', 'true');
                      } catch { }
                      setDesignMode('ESQ');
                      setOpenModal(null);
                    }}
                  >
                    <ModeTitle>ESQ</ModeTitle>
                  </ModeOption>
                </ModeGrid>
              )}

              {openModal === 'art' && (
                <Art
                  key={`art-${forcedGranite || 'none'}-${currentModelId || 'none'}-${(currentDesign as any)?.selectedSizeId || 'none'}`}
                  onClose={() => setOpenModal(null)}
                  onArtworkSelect={handleArtworkSelect}
                  setImageGridActive={setArtImageGridActive}
                  forcedGranite={forcedGranite}
                />
              )}

              {openModal === 'artwork-mapping' && (
                <ArtworkMapping onClose={() => setOpenModal(null)} />
              )}

              {openModal === 'vases' && (
                <Vases
                  onClose={() => setOpenModal(null)}
                  onVaseSelect={handleVaseSelect}
                  esqModalVasePath={effectiveDesignMode === 'ESQ' ? selectedEsqModal?.vase_path || 'esq_modals/Serptop Markers/pjb/Av01-Black.png' : undefined}
                />
              )}

              {openModal === 'esq-modals' && (
                <EsqModals
                  onSelect={handleEsqModalSelect}
                  colorId={selectedColorId ?? null}
                  onClose={() => setOpenModal(null)}
                />
              )}

              {openModal === 'esq-templates' && (
                <EsqTemplates
                  modalId={selectedEsqModal?.id}
                  colorId={selectedColorId || 0}
                  onSelect={(tpl) => handleEsqTemplateSelect({
                    ...tpl,
                    modalId: selectedEsqModal?.id,
                    colorId: selectedColorId || 0
                  })}
                  onClose={() => setOpenModal(null)}
                />
              )}

              {openModal === 'esq-text' && (
                <EsqText
                  value={esqTextValues}
                  onChange={handleEsqTextChange}
                  limits={esqFieldCharacterLimits}
                  fullNameMode={false}
                  sharedLimit={sharedNameLimit}
                  isSerptopMode={isEsqSerptopModal}
                />
              )}

              {openModal === 'faq' && (
                <FAQList>
                  {FAQ_ITEMS.map((item, idx) => (
                    <FAQItem key={item.question}>
                      <FAQQuestion>{`Q${idx + 1}. ${item.question}`}</FAQQuestion>
                      <FAQAnswer>{item.answer}</FAQAnswer>
                    </FAQItem>
                  ))}
                </FAQList>
              )}


              {openModal === 'porcelain' && (
                <Porcelain onClose={() => setOpenModal(null)} />
              )}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Text Modal: Always mounted to accurately calculate text snap positions silently in the background */}
      <ModalOverlay
        style={{ display: (openModal === 'text') ? 'block' : 'none' }}
      >
        <ModalCard
          $isTextModal={true}
          onClick={(e) => e.stopPropagation()}
          style={{ display: (openModal === 'text') ? 'flex' : 'none' }}
        >
          <ModalHeader>
            <span>{currentDesign?.activeSide === 'back' ? 'Back Side Text' : 'Personal Details'}</span>
            <CloseBtn onClick={() => setOpenModal(null)}>✕</CloseBtn>
          </ModalHeader>
          <ModalBody $noPadding={true}>
            <Text
              onClose={() => setOpenModal(null)}
              onConfirm={handleTextConfirm}
              isTemplateSelected={isTemplateSelected}
              selectedColorIndex={selectedColorIndex ?? 0}
              colors={colors}
              preloadedVerses={cachedVerses}
              preloadedCategories={cachedCategories}
            />
          </ModalBody>
        </ModalCard>
      </ModalOverlay>

      {/* Shapes Modal: keep mounted after first open to avoid repeated image decode delays */}
      {/* Note: !isLoading intentionally removed - display:none already hides it during loading.
           Keeping it mounted prevents model thumbnails from being re-fetched on every model change. */}
      {hasOpenedShapes && (
        <ModalOverlay
          style={{ display: (openModal === 'shapes') ? 'block' : 'none' }}
        >
          <ModalCard
            $isShapesModal={openModal === 'shapes'}
            onClick={(e) => e.stopPropagation()}
            style={{ display: (openModal === 'shapes') ? 'flex' : 'none' }}
          >
            <ModalHeader>
              <span>Select Model</span>
              <CloseBtn onClick={() => setOpenModal(null)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <Shapes
                onClose={() => setOpenModal(null)}
                onShapeSelect={handleShapeSelect}
                selectedColorId={selectedColorId ?? null}
                forcedGranite={forcedGranite}
                initialModels={cachedModels}
                incrementTemplateResetKey={incrementTemplateResetKey}
                setIsLoading={setIsLoading}
                autoSelectFirstShape={isInitialLoad}
              />
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={imageUploadRef}
        onChange={handleImageUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />

    </Panel>
  );
};

export default LeftPanel;
